import type { ProjectSearchScope } from '@projectSearch/searchScope';
import type { ProjectListSort } from '@projectSearch/searchSort';
import {
  encodeProjectFilter,
  type ProjectFilter,
  type ProjectFilterCounts,
  type ProjectFilterMenuField,
  type ProjectFilterOption,
  type ProjectListTab,
  type PROJECT_TYPE_CODES,
} from '@projectSearch/projectFilter';
import { projectSearchCountsSchema, type ProjectSearchCounts } from './projectSearchModel';
import { projectFilterCountsSchema, projectFilterOptionsSchema } from './projectFilterModel';
import { readerPlannedCountsSchema, readerPlannedPageSchema } from './readerPlannedModel';
import type { DeadlineField } from '@stages/stages';
import { API_BASE_URL } from '@/config/serverConfig';
import { sheetWorkbookSchema, type SheetWorkbook } from './sheetModel';
import {
  glossaryDataSchema,
  startTextRunResponseSchema,
  stressEntrySchema,
  suggestionsPageSchema,
  type CreateEntryInput,
  type EntryPatch,
  type GlossaryData,
  type StressEntry,
  type SuggestionSection,
  type SuggestionsPage,
  type TextRun,
} from './glossaryModel';
import {
  applyResponseSchema,
  importResponseSchema,
  type GlossaryImport,
  type ImportCounts,
  type ImportDecisions,
} from './glossaryImportModel';
import { handleAuthErrorResponse, resolveAuthErrorResponse } from '@/lib/authSession';
import { pendingProjectCreateFor } from '@/lib/pendingProjectCreates';
import { fileNameFromDisposition, type DownloadedFile } from './download';
import { stressIndexResponseSchema, wordContextsSchema, wordFormsSchema, type StressIndexFetch, type WordContext, type WordForms } from './stressIndexModel';
import type { AccessCapabilities, AccessPreview } from '@/types/access';

async function handleXhrAuthError(xhr: XMLHttpRequest): Promise<boolean> {
  if (xhr.status !== 401 && xhr.status !== 403) return false;
  let body: unknown = { error: xhr.statusText };
  try {
    body = JSON.parse(xhr.responseText);
  } catch {
    /* use default body */
  }
  return handleAuthErrorResponse(xhr.status, body);
}
import type {
  AgentChatModelOption,
  AgentChatQuotaSnapshot,
} from '@/components/Messenger/agent/agentChatSharedTypes';

export class ApiError extends Error {
  readonly status: number;
  /** Тело ответа при ошибке (JSON), если удалось распарсить. */
  readonly rawJson: unknown | undefined;

  constructor(status: number, message: string, rawJson?: unknown) {
    super(message);
    this.status = status;
    this.rawJson = rawJson;
    this.name = 'ApiError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Коды ошибок черновиков — server/src/routes/projects/draftsContract.ts. */
const PROJECT_DRAFT_ERROR_CODES = ['PROJECT_NAME_REQUIRED', 'PROJECT_NOT_DRAFT', 'PROJECT_IS_DRAFT', 'PROJECT_ID_TAKEN'] as const;
export type ProjectDraftErrorCode = (typeof PROJECT_DRAFT_ERROR_CODES)[number];

/** Код ошибки черновика из ответа сервера; null — это не ошибка черновика. */
export function projectDraftErrorCode(err: unknown): ProjectDraftErrorCode | null {
  if (!(err instanceof ApiError) || typeof err.rawJson !== 'object' || err.rawJson === null) return null;
  const code = (err.rawJson as { code?: unknown }).code;
  return PROJECT_DRAFT_ERROR_CODES.find((known) => known === code) ?? null;
}

/**
 * Запрос к проекту, чьё создание ещё в пути (черновик с id клиента), уходит после него — иначе сервер ответил бы 404.
 * Повтор после 401 не ждёт: первый заход уже дождался. null — ждать нечего: запрос уходит синхронно, как и без черновиков.
 */
const pendingCreateBefore = (path: string, retried: boolean): Promise<void> | null =>
  retried ? null : pendingProjectCreateFor(path);

async function request<T>(path: string, options?: RequestInit, retried = false): Promise<T> {
  const pending = pendingCreateBefore(path, retried);
  if (pending) await pending;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers,
  });
  if (!res.ok) return rejectResponse<T>(res, path, retried, () => request<T>(path, options, true));
  if (res.status === 204) return undefined as T;
  return res.json();
}

/**
 * Неуспешный ответ: 401 — обновление сессии и один повтор (retry); сессия кончилась — undefined;
 * сервер не ответил на обновление — ApiError 503, сессия остаётся; иначе ApiError.
 */
async function rejectResponse<T>(res: Response, path: string, retried: boolean, retry: () => Promise<T>): Promise<T> {
  const err = await res.json().catch(() => ({ error: res.statusText }));
  if (res.status === 401 && !retried && path !== '/auth/refresh' && path !== '/auth/login') {
    const outcome = await resolveAuthErrorResponse(res.status, err);
    if (outcome === 'refreshed') return retry();
    if (outcome === 'ended') return undefined as T;
    throw new ApiError(503, 'Нет связи с сервером', err);
  }
  if (await handleAuthErrorResponse(res.status, err, { skipRefresh: retried })) {
    return undefined as T;
  }
  const message = typeof err.error === 'string' && err.error.trim() ? err.error.trim() : res.statusText;
  throw new ApiError(res.status, message, err);
}

/** Ревизия файлов проекта: заголовок X-Files-Revision (сервер: routes/projects/files.ts); нет — null */
export type FilesRevision = number | null;

/** Запрос, чей ответ несёт ревизию файлов проекта: тело и ревизия одного снимка */
async function requestWithFilesRevision<T>(path: string, options?: RequestInit, retried = false): Promise<{ body: T; revision: FilesRevision }> {
  const pending = pendingCreateBefore(path, retried);
  if (pending) await pending;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options?.headers as Record<string, string>) },
  });
  if (!res.ok) return rejectResponse(res, path, retried, () => requestWithFilesRevision<T>(path, options, true));
  const header = res.headers.get('X-Files-Revision');
  const revision = header !== null && /^\d+$/.test(header) ? Number(header) : null;
  return { body: (res.status === 204 ? undefined : await res.json()) as T, revision };
}

type ConditionalResult = { notModified: true } | { notModified: false; etag: string | null; body: unknown };

/**
 * GET с ETag: If-None-Match — версия, которая уже есть у клиента; 304 — она не изменилась. Кэш браузера выключен:
 * версию и данные держит TanStack Query, иначе браузер сам превращал бы 304 в 200 со старым телом.
 */
async function requestConditional(path: string, etag: string | null, retried = false): Promise<ConditionalResult | undefined> {
  const pending = pendingCreateBefore(path, retried);
  if (pending) await pending;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    cache: 'no-store',
    headers: etag ? { 'If-None-Match': etag } : {},
  });
  if (res.status === 304) return { notModified: true };
  if (!res.ok) return rejectResponse(res, path, retried, () => requestConditional(path, etag, true));
  return { notModified: false, etag: res.headers.get('ETag'), body: await res.json() };
}

/** GET файла (не JSON): тело как Blob и имя из Content-Disposition; ошибки — как у request. */
async function requestFile(path: string, retried = false): Promise<DownloadedFile | undefined> {
  const pending = pendingCreateBefore(path, retried);
  if (pending) await pending;
  const res = await fetch(`${API_BASE_URL}${path}`, { credentials: 'include', cache: 'no-store' });
  if (!res.ok) return rejectResponse(res, path, retried, () => requestFile(path, true));
  return { blob: await res.blob(), fileName: fileNameFromDisposition(res.headers.get('Content-Disposition')) };
}

/** Загрузка одного файла в поле `file` с ходом загрузки (XHR — у fetch нет прогресса отправки) */
function uploadAudioFile<T>(path: string, file: File, onProgress?: (pct: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}${path}`);
    xhr.withCredentials = true;

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      void (async () => {
        if (await handleXhrAuthError(xhr)) return;
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as T);
          } catch {
            reject(new Error('Некорректный ответ сервера'));
          }
        } else {
          try {
            const err = JSON.parse(xhr.responseText);
            reject(new Error(err.error || xhr.statusText));
          } catch {
            reject(new Error(xhr.statusText));
          }
        }
      })();
    });

    xhr.addEventListener('error', () => reject(new Error('Ошибка сети при загрузке файла')));
    xhr.addEventListener('abort', () => reject(new Error('Загрузка отменена')));

    xhr.send(formData);
  });
}

export const api = {
  projects: {
    list: (params?: {
      q?: string;
      columnQ?: string;
      searchIn?: string;
      readerId?: string;
      authorId?: string;
      limit?: number;
      offset?: number;
      excludeReady?: boolean;
      /** Только с q: в работе, готовые или все (по умолчанию) — server/src/shared/projectSearch */
      scope?: ProjectSearchScope;
      /** Порядок найденного (новый дизайн) — server/src/shared/projectSearch/searchSort; без него — свежие правки сверху */
      sort?: ProjectListSort;
      /** Фильтр списка (новый дизайн) — server/src/shared/projectSearch/projectFilter; с ним ответ — { items, hasMore } */
      filter?: ProjectFilter;
      /** Вкладка списка без q: в работе, готовые или архив; с q вместо неё действует scope */
      tab?: ProjectListTab;
    }) => {
      const search = new URLSearchParams();
      if (params?.q?.trim()) search.set('q', params.q.trim());
      if (params?.columnQ?.trim()) search.set('columnQ', params.columnQ.trim());
      if (params?.searchIn?.trim()) search.set('searchIn', params.searchIn.trim());
      if (params?.readerId?.trim()) search.set('readerId', params.readerId.trim());
      if (params?.authorId?.trim()) search.set('authorId', params.authorId.trim());
      if (params?.limit != null) search.set('limit', String(params.limit));
      if (params?.offset != null) search.set('offset', String(params.offset));
      if (params?.excludeReady) search.set('excludeReady', '1');
      if (params?.scope && params.scope !== 'all') search.set('scope', params.scope);
      if (params?.sort) search.set('sort', params.sort);
      if (params?.tab) search.set('tab', params.tab);
      for (const [key, value] of encodeProjectFilter(params?.filter ?? {})) search.set(key, value);
      const query = search.toString();
      return request<Project[] | { items: Project[]; hasMore: boolean }>(`/projects${query ? `?${query}` : ''}`);
    },
    readyMonths: () =>
      request<ReadyMonthStatRow[]>(`/projects/ready-months`),
    readyByMonth: (year: number, month: number) =>
      request<Project[]>(`/projects/ready-by-month/${year}/${month}`),
    /** Числа на вкладках поиска нового дизайна (Все / В работе / Готовые) для запроса q */
    searchCounts: async (q: string): Promise<ProjectSearchCounts> =>
      projectSearchCountsSchema.parse(await request<unknown>(`/projects/search-counts?${new URLSearchParams({ q: q.trim() })}`)),
    /** Числа вкладок при включённом фильтре: с фильтром и без него (без q — В работе / Готовые / Архив, с q — Все / В работе / Готовые) */
    filterCounts: async (params: { filter: ProjectFilter; q?: string }): Promise<ProjectFilterCounts> => {
      const search = new URLSearchParams(encodeProjectFilter(params.filter));
      if (params.q?.trim()) search.set('q', params.q.trim());
      return projectFilterCountsSchema.parse(await request<unknown>(`/projects/filter-counts?${search}`));
    },
    /**
     * Варианты подменю поля: из проектов вкладки tab (по умолчанию «В работе») или, с search, области scope;
     * filter — целиком (само поле сервер в числах не учитывает). search — текст поиска проектов, q — поиск по подписи варианта.
     */
    filterOptions: async (params: {
      field: ProjectFilterMenuField;
      filter: ProjectFilter;
      tab?: ProjectListTab;
      scope?: ProjectSearchScope;
      search?: string;
      q?: string;
    }): Promise<ProjectFilterOption[]> => {
      const search = new URLSearchParams([['field', params.field], ...encodeProjectFilter(params.filter)]);
      if (params.tab) search.set('tab', params.tab);
      if (params.scope && params.scope !== 'all') search.set('scope', params.scope);
      if (params.search?.trim()) search.set('search', params.search.trim());
      if (params.q?.trim()) search.set('q', params.q.trim());
      const { items } = projectFilterOptionsSchema.parse(await request<unknown>(`/projects/filter-options?${search}`));
      return items;
    },
    archivedCount: () => request<{ count: number }>(`/projects/archived-count`),
    archivedList: () => request<Project[]>(`/projects/archived`),
    get: (id: string) => request<Project>(`/projects/${id}`),
    create: (data: CreateProjectData) => request<Project>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    /**
     * Новый пустой черновик автора с id, выданным клиентом (карточка открывается до ответа). Повтор с тем же id — тот же
     * черновик; id занят чем-то другим — 409 PROJECT_ID_TAKEN.
     */
    createDraft: (id: string) => request<Project>('/projects/drafts', { method: 'POST', body: JSON.stringify({ id }) }),
    /** Черновики автора, свежие правки сверху. */
    listDrafts: () => request<Project[]>('/projects/drafts'),
    /** «Создать проект»: ошибки PROJECT_NAME_REQUIRED (400), PROJECT_NOT_DRAFT (409) — см. projectDraftErrorCode. */
    publish: (id: string, name: string) =>
      request<Project>(`/projects/${id}/publish`, { method: 'POST', body: JSON.stringify({ name }) }),
    /** Уход из черновика: пустой удаляется (deleted: true), заполненный остаётся. */
    discardIfEmpty: (id: string) =>
      request<{ deleted: boolean }>(`/projects/${id}/discard-if-empty`, { method: 'POST', body: JSON.stringify({}) }),
    update: (id: string, data: UpdateProjectData) => request<Project>(`/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    /** Треки проекта: tracks — для блока карточки (с их проектом Фонотеки), track_ids — для выбора по всему каталогу */
    getFonotekaTracks: (id: string) =>
      request<ProjectFonotekaTracks>(`/projects/${id}/fonoteka-tracks`),
    putFonotekaTracks: (id: string, track_ids: string[]) =>
      request<{ track_ids: string[] }>(`/projects/${id}/fonoteka-tracks`, {
        method: 'PUT',
        body: JSON.stringify({ track_ids }),
      }),
    /** Добавить трек Фонотеки в проект (повтор безвреден) */
    addFonotekaTrack: (id: string, trackId: string) =>
      request<ProjectFonotekaTracks>(`/projects/${id}/fonoteka-tracks/${trackId}`, { method: 'POST' }),
    /** Убрать трек из проекта; из Фонотеки он не удаляется */
    removeFonotekaTrack: (id: string, trackId: string) =>
      request<ProjectFonotekaTracks>(`/projects/${id}/fonoteka-tracks/${trackId}`, { method: 'DELETE' }),
    /** Загрузить трек с компьютера в проект: ложится в проект Фонотеки (нет — создаётся) и сразу добавляется в проект */
    uploadFonotekaTrack: (id: string, file: File, onProgress?: (pct: number) => void) =>
      uploadAudioFile<ProjectFonotekaTracks & { track: FonotekaTrack }>(`/projects/${id}/fonoteka-tracks/upload`, file, onProgress),
    delete: (id: string) => request<void>(`/projects/${id}`, { method: 'DELETE' }),
    restore: (id: string) =>
      request<Project>(`/projects/${id}/restore`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    cellCommentSummaries: (projectIds: string[]) =>
      request<{
        summaries: Record<string, Record<string, number>>;
        meta?: { requested: number; chunkSize: number };
      }>('/projects/cell-comment-summaries', {
        method: 'POST',
        body: JSON.stringify({ projectIds }),
      }),
    /** Сводка заметок / файлов / сессий для строк таблицы (без тяжёлых списков в GET /projects). */
    projectResourceSummaries: (projectIds: string[]) =>
      request<{
        summaries: Record<string, ProjectResourceSummary>;
        meta?: { requested: number; chunkSize: number };
      }>('/projects/project-resource-summaries', {
        method: 'POST',
        body: JSON.stringify({ projectIds }),
      }),
    listCellComments: (
      projectId: string,
      columnId: string,
      params?: { limit?: number; offset?: number }
    ) => {
      const search = new URLSearchParams();
      search.set('columnId', columnId);
      if (params?.limit != null) search.set('limit', String(params.limit));
      if (params?.offset != null) search.set('offset', String(params.offset));
      return request<CellCommentsListResponse>(
        `/projects/${projectId}/cell-comments?${search.toString()}`
      );
    },
    createCellComment: (
      projectId: string,
      data: { column_id: string; body: string; reply_to_id?: string | null }
    ) =>
      request<CellComment>(`/projects/${projectId}/cell-comments`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateCellComment: (projectId: string, commentId: string, data: { body: string }) =>
      request<CellComment>(`/projects/${projectId}/cell-comments/${commentId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    deleteCellComment: (projectId: string, commentId: string) =>
      request<void>(`/projects/${projectId}/cell-comments/${commentId}`, { method: 'DELETE' }),
    getStatusEvents: (id: string) => request<ProjectStatusEvent[]>(`/projects/${id}/status-events`),
    getNotes: (id: string) => request<ProjectNote[]>(`/projects/${id}/notes`),
    getChanges: (
      id: string,
      params?: { columnId?: string; before_cursor?: string; limit?: number }
    ) => {
      const search = new URLSearchParams();
      if (params?.columnId) search.set('columnId', params.columnId);
      if (params?.before_cursor) search.set('before_cursor', params.before_cursor);
      if (params?.limit != null) search.set('limit', String(params.limit));
      const q = search.toString();
      return request<import('@/features/project-change-history/types').ProjectChangeHistoryPage>(
        `/projects/${id}/changes${q ? `?${q}` : ''}`
      );
    },
    getChangesAvailability: (id: string) =>
      request<import('@/features/project-change-history/types').UndoRedoAvailability>(
        `/projects/${id}/changes/availability`
      ),
    undo: (projectId: string) =>
      request<import('@/features/project-change-history/types').UndoRedoResult>(
        `/projects/${projectId}/undo`,
        { method: 'POST', body: JSON.stringify({}) }
      ),
    undoGlobal: () =>
      request<import('@/features/project-change-history/types').UndoRedoResult>(`/projects/undo`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    redo: (projectId: string) =>
      request<import('@/features/project-change-history/types').UndoRedoResult>(
        `/projects/${projectId}/redo`,
        { method: 'POST', body: JSON.stringify({}) }
      ),
  },
  sessions: {
    list: (params?: {
      from?: string;
      to?: string;
      projectId?: string;
      excludeMontage?: boolean;
      readerIds?: string[];
      excludeProjectId?: string;
    }) => {
      const search = new URLSearchParams();
      if (params?.from) search.set('from', params.from);
      if (params?.to) search.set('to', params.to);
      if (params?.projectId) search.set('projectId', params.projectId);
      if (params?.excludeMontage) search.set('excludeMontage', '1');
      if (params?.readerIds?.length) search.set('readerIds', params.readerIds.join(','));
      if (params?.excludeProjectId) search.set('excludeProjectId', params.excludeProjectId);
      const q = search.toString();
      return request<Session[]>(`/sessions${q ? `?${q}` : ''}`);
    },
    /** «В других проектах» (новый дизайн): порция запланированных сессий чтецов в других проектах, от ближайшей */
    readerPlanned: async (params: { readerIds: readonly string[]; excludeProjectId: string; cursor: string | null }) => {
      const search = new URLSearchParams({ readerIds: params.readerIds.join(','), excludeProjectId: params.excludeProjectId });
      if (params.cursor) search.set('cursor', params.cursor);
      return readerPlannedPageSchema.parse(await request<unknown>(`/sessions/reader-planned?${search}`));
    },
    /** Числа «В других проектах»: всего и по чтецам */
    readerPlannedCounts: async (params: { readerIds: readonly string[]; excludeProjectId: string }) => {
      const search = new URLSearchParams({ readerIds: params.readerIds.join(','), excludeProjectId: params.excludeProjectId });
      return readerPlannedCountsSchema.parse(await request<unknown>(`/sessions/reader-planned/counts?${search}`));
    },
    create: (data: Partial<Session>) => request<Session>('/sessions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    update: (id: string, data: Partial<Session>) => request<Session>(`/sessions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    delete: (id: string) => request<void>(`/sessions/${id}`, { method: 'DELETE' }),
    restore: (id: string) =>
      request<Session>(`/sessions/${id}/restore`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    sessionCommentSummaries: (sessionIds: string[]) =>
      request<{
        summaries: Record<string, number>;
        meta: { requested: number; chunkSize: number };
      }>('/sessions/comment-summaries', {
        method: 'POST',
        body: JSON.stringify({ sessionIds }),
      }),
    listSessionComments: (sessionId: string, params?: { limit?: number; offset?: number }) => {
      const search = new URLSearchParams();
      if (params?.limit != null) search.set('limit', String(params.limit));
      if (params?.offset != null) search.set('offset', String(params.offset));
      const q = search.toString();
      return request<SessionCommentsListResponse>(
        `/sessions/${sessionId}/comments${q ? `?${q}` : ''}`
      );
    },
    createSessionComment: (sessionId: string, data: { body: string; reply_to_id?: string | null }) =>
      request<SessionComment>(`/sessions/${sessionId}/comments`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateSessionComment: (sessionId: string, commentId: string, data: { body: string }) =>
      request<SessionComment>(`/sessions/${sessionId}/comments/${commentId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    deleteSessionComment: (sessionId: string, commentId: string) =>
      request<void>(`/sessions/${sessionId}/comments/${commentId}`, { method: 'DELETE' }),
    getChanges: (
      id: string,
      params?: { columnId?: string; before_cursor?: string; limit?: number }
    ) => {
      const search = new URLSearchParams();
      if (params?.columnId) search.set('columnId', params.columnId);
      if (params?.before_cursor) search.set('before_cursor', params.before_cursor);
      if (params?.limit != null) search.set('limit', String(params.limit));
      const q = search.toString();
      return request<import('@/features/session-change-history/types').SessionChangeHistoryPage>(
        `/sessions/${id}/history${q ? `?${q}` : ''}`
      );
    },
    getChangesAvailability: (id: string) =>
      request<import('@/features/session-change-history/types').UndoRedoAvailability>(
        `/sessions/${id}/undo-redo-availability`
      ),
    undo: (id: string) =>
      request<import('@/features/session-change-history/types').UndoRedoResult>(
        `/sessions/${id}/undo`,
        { method: 'POST', body: JSON.stringify({}) }
      ),
    undoGlobal: () =>
      request<import('@/features/session-change-history/types').UndoRedoResult>(`/sessions/undo`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    redo: (id: string) =>
      request<import('@/features/session-change-history/types').UndoRedoResult>(
        `/sessions/${id}/redo`,
        { method: 'POST', body: JSON.stringify({}) }
      ),
  },
  persons: {
    /** readersOnly — только люди из каталога чтецов (фильтр до limit, а не по готовой странице) */
    list: (q?: string, opts?: { limit?: number; offset?: number; readersOnly?: boolean }) => {
      const search = new URLSearchParams();
      if (q?.trim()) search.set('q', q.trim());
      if (opts?.readersOnly) search.set('readers_only', '1');
      if (opts?.limit != null) search.set('limit', String(opts.limit));
      if (opts?.offset != null) search.set('offset', String(opts.offset));
      const query = search.toString();
      return request<{ items: PersonSearchResult[]; hasMore: boolean }>(`/persons${query ? `?${query}` : ''}`);
    },
    lookup: (params: { first_name: string; last_name: string }) => {
      const q = new URLSearchParams();
      q.set('first_name', params.first_name);
      q.set('last_name', params.last_name);
      return request<PersonLookupResponse>(`/persons/lookup?${q.toString()}`);
    },
    get: (id: string) => request<Person>(`/persons/${id}`),
    patch: (id: string, data: { first_name?: string; last_name?: string; email?: string | null; phone?: string | null }) =>
      request<Person>(`/persons/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    roleSnapshot: (id: string) => request<PersonRoleSnapshot>(`/persons/${id}/role-snapshot`),
    create: (data: { first_name: string; last_name: string; email?: string; phone?: string }) =>
      request<Person>('/persons', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    delete: (id: string) => request<void>(`/persons/${id}`, { method: 'DELETE' }),
    listNotes: (personId: string) => request<PersonNote[]>(`/persons/${personId}/notes`),
    createNote: (personId: string, data: { note_content: string; created_by?: string | null; display_order?: number }) =>
      request<PersonNote>(`/persons/${personId}/notes`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateNote: (personId: string, noteId: string, data: { note_content?: string; display_order?: number }) =>
      request<PersonNote>(`/persons/${personId}/notes/${noteId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    deleteNote: (personId: string, noteId: string) =>
      request<void>(`/persons/${personId}/notes/${noteId}`, { method: 'DELETE' }),
    catalogSave: async (data: PersonCatalogSaveRequest): Promise<PersonCatalogSaveResponse> => {
      const res = await fetch(`${API_BASE_URL}/persons/catalog-save`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const parsed = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok) {
        if (await handleAuthErrorResponse(res.status, parsed)) {
          return parsed as unknown as PersonCatalogSaveResponse;
        }
        const msg = typeof parsed.error === 'string' ? parsed.error : res.statusText;
        const err = new Error(msg) as Error & { status: number; code?: string; person_id?: string };
        err.status = res.status;
        if (typeof parsed.code === 'string') err.code = parsed.code;
        if (typeof parsed.person_id === 'string') err.person_id = parsed.person_id;
        throw err;
      }
      return parsed as unknown as PersonCatalogSaveResponse;
    },
  },
  readerTags: {
    list: () => request<ReaderTag[]>('/reader-tags'),
    create: (data: { name: string; color: string; tag_group?: string; display_order?: number }) =>
      request<ReaderTag>('/reader-tags', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: Partial<{ name: string; color: string; tag_group: string; display_order: number }>) =>
      request<ReaderTag>(`/reader-tags/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (id: string) => request<void>(`/reader-tags/${id}`, { method: 'DELETE' }),
    renameGroup: (from: string, to: string) =>
      request<{ updated: number }>('/reader-tags/group/rename', {
        method: 'PATCH',
        body: JSON.stringify({ from, to }),
      }),
    deleteGroup: (tag_group: string) =>
      request<{ deleted: number }>('/reader-tags/group', {
        method: 'DELETE',
        body: JSON.stringify({ tag_group }),
      }),
  },
  musicTags: {
    list: () => request<MusicTag[]>('/music-tags'),
    create: (data: { name: string; color: string; tag_group?: string; display_order?: number }) =>
      request<MusicTag>('/music-tags', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: Partial<{ name: string; color: string; tag_group: string; display_order: number }>) =>
      request<MusicTag>(`/music-tags/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (id: string) => request<void>(`/music-tags/${id}`, { method: 'DELETE' }),
    renameGroup: (from: string, to: string) =>
      request<{ updated: number }>('/music-tags/group/rename', {
        method: 'PATCH',
        body: JSON.stringify({ from, to }),
      }),
    deleteGroup: (tag_group: string) =>
      request<{ deleted: number }>('/music-tags/group', {
        method: 'DELETE',
        body: JSON.stringify({ tag_group }),
      }),
  },
  miniApps: {
    list: () => request<MiniAppCatalogRow[]>('/mini-apps'),
    like: (id: string) =>
      request<MiniAppCatalogRow>(`/mini-apps/${encodeURIComponent(id)}/likes`, { method: 'POST' }),
    unlike: (id: string) =>
      request<MiniAppCatalogRow>(`/mini-apps/${encodeURIComponent(id)}/likes`, { method: 'DELETE' }),
  },
  readers: {
    list: (q?: string, opts?: { limit?: number; offset?: number }) => {
      const search = new URLSearchParams();
      if (q?.trim()) search.set('q', q.trim());
      if (opts?.limit != null) search.set('limit', String(opts.limit));
      if (opts?.offset != null) search.set('offset', String(opts.offset));
      const query = search.toString();
      return request<Reader[] | { items: Reader[]; hasMore: boolean }>(`/readers${query ? `?${query}` : ''}`);
    },
    deletionContext: (personId: string) =>
      request<ReaderDeletionContext>(`/readers/${personId}/deletion-context`),
    create: (data: {
      person_id?: string;
      first_name?: string;
      last_name?: string;
      prices?: ReaderPriceInput[];
      links?: ReaderLinkInput[];
      studio_id?: string | null;
      legal_entity_id?: string | null;
    }) =>
      request<Reader>('/readers', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (personId: string, data: Partial<{
      first_name: string;
      last_name: string;
      email: string | null;
      phone: string | null;
      prices: ReaderPriceInput[];
      name_note: string | null;
      cooperation_state: string;
      links: ReaderLinkInput[];
      studio_id: string | null;
      legal_entity_id: string | null;
      tag_ids?: string[];
    }>) =>
      request<Reader>(`/readers/${personId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (personId: string) => request<void>(`/readers/${personId}`, { method: 'DELETE' }),
    listLinks: (personId: string) => request<ReaderLink[]>(`/readers/${personId}/links`),
    createLink: (personId: string, data: ReaderLinkInput) =>
      request<ReaderLink>(`/readers/${personId}/links`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateLink: (personId: string, linkId: string, data: Partial<ReaderLinkInput>) =>
      request<ReaderLink>(`/readers/${personId}/links/${linkId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    deleteLink: (personId: string, linkId: string) =>
      request<void>(`/readers/${personId}/links/${linkId}`, { method: 'DELETE' }),
    cellCommentSummaries: (readerPersonIds: string[]) =>
      request<{
        summaries: Record<string, Record<string, number>>;
        meta?: { requested: number; chunkSize: number };
      }>('/readers/cell-comment-summaries', {
        method: 'POST',
        body: JSON.stringify({ readerPersonIds }),
      }),
    personNotesSummaries: (readerPersonIds: string[]) =>
      request<{
        summaries: Record<string, ReaderPersonNotesSummary>;
        meta?: { requested: number; chunkSize: number };
      }>('/readers/person-notes-summaries', {
        method: 'POST',
        body: JSON.stringify({ readerPersonIds }),
      }),
    fileSummaries: (readerPersonIds: string[]) =>
      request<{
        summaries: Record<string, ReaderFilesSummary>;
        meta?: { requested: number; chunkSize: number };
      }>('/readers/file-summaries', {
        method: 'POST',
        body: JSON.stringify({ readerPersonIds }),
      }),
    listCellComments: (
      personId: string,
      columnId: string,
      params?: { limit?: number; offset?: number }
    ) => {
      const search = new URLSearchParams();
      search.set('columnId', columnId);
      if (params?.limit != null) search.set('limit', String(params.limit));
      if (params?.offset != null) search.set('offset', String(params.offset));
      return request<ReaderCellCommentsListResponse>(
        `/readers/${personId}/cell-comments?${search.toString()}`
      );
    },
    createCellComment: (personId: string, data: { column_id: string; body: string }) =>
      request<ReaderCellComment>(`/readers/${personId}/cell-comments`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateCellComment: (personId: string, commentId: string, data: { body: string }) =>
      request<ReaderCellComment>(`/readers/${personId}/cell-comments/${commentId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    deleteCellComment: (personId: string, commentId: string) =>
      request<void>(`/readers/${personId}/cell-comments/${commentId}`, { method: 'DELETE' }),
  },
  users: {
    list: (q?: string, opts?: { limit?: number; offset?: number }) => {
      const search = new URLSearchParams();
      if (q?.trim()) search.set('q', q.trim());
      if (opts?.limit != null) search.set('limit', String(opts.limit));
      if (opts?.offset != null) search.set('offset', String(opts.offset));
      const query = search.toString();
      return request<{ items: User[]; hasMore: boolean }>(`/users${query ? `?${query}` : ''}`);
    },
    get: (id: string) => request<User>(`/users/${id}`),
    create: (data: {
      person_id?: string;
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
      role?: string;
      access?: string;
    }) =>
      request<User>('/users', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: { first_name?: string; last_name?: string; email?: string; phone?: string; role?: string; access?: string; is_active?: boolean }) =>
      request<User>(`/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<void>(`/users/${id}`, { method: 'DELETE' }),
  },
  projectStatusEvents: {
    list: (projectId: string) => request<ProjectStatusEvent[]>(`/projects/${projectId}/status-events`),
    create: (projectId: string, data: { event_type: string; changed_by_user_id: string }) =>
      request<ProjectStatusEvent>(`/projects/${projectId}/status-events`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    get: (projectId: string, eventId: string) =>
      request<ProjectStatusEvent>(`/projects/${projectId}/status-events/${eventId}`),
    update: (projectId: string, eventId: string, data: { event_type?: string; occurred_at?: string; changed_by_user_id?: string }) =>
      request<ProjectStatusEvent>(`/projects/${projectId}/status-events/${eventId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (projectId: string, eventId: string) =>
      request<void>(`/projects/${projectId}/status-events/${eventId}`, { method: 'DELETE' }),
  },
  projectNotes: {
    list: (projectId: string) => request<ProjectNote[]>(`/projects/${projectId}/notes`),
    create: (projectId: string, data: { note_id?: string; note_content: string; created_by?: string | null; display_order?: number }) =>
      request<ProjectNote>(`/projects/${projectId}/notes`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (projectId: string, noteId: string, data: { note_content?: string; display_order?: number }) =>
      request<ProjectNote>(`/projects/${projectId}/notes/${noteId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (projectId: string, noteId: string) =>
      request<void>(`/projects/${projectId}/notes/${noteId}`, { method: 'DELETE' }),
  },
  projectTasks: {
    list: (projectId: string) => request<ProjectTask[]>(`/projects/${projectId}/tasks`),
    create: (projectId: string, data: { task_id: string; title: string }) =>
      request<ProjectTask>(`/projects/${projectId}/tasks`, { method: 'POST', body: JSON.stringify(data) }),
    update: (projectId: string, taskId: string, data: ProjectTaskPatch) =>
      request<ProjectTask>(`/projects/${projectId}/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (projectId: string, taskId: string) =>
      request<void>(`/projects/${projectId}/tasks/${taskId}`, { method: 'DELETE' }),
  },
  /** Глоссарий ударений; ответы проверяются схемой (glossaryModel.ts) — пустой ответ при конце сессии тоже ошибка */
  glossary: {
    get: async (projectId: string): Promise<GlossaryData> =>
      glossaryDataSchema.parse(await request<unknown>(`/projects/${projectId}/glossary`)),
    suggestions: async (projectId: string, section: SuggestionSection, cursor: string | null): Promise<SuggestionsPage> =>
      suggestionsPageSchema.parse(
        await request<unknown>(
          `/projects/${projectId}/glossary/suggestions?limit=50&section=${section}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`
        )
      ),
    /** «Разобрать»: читает выбранные файлы «Текст», прежний разбор заменяется (202) */
    startTextRun: async (projectId: string, fileIds: string[]): Promise<TextRun> =>
      startTextRunResponseSchema.parse(
        await request<unknown>(`/projects/${projectId}/glossary/text-runs`, { method: 'POST', body: JSON.stringify({ file_ids: fileIds }) })
      ).text_run,
    create: async (projectId: string, body: CreateEntryInput): Promise<StressEntry> =>
      stressEntrySchema.parse(await request<unknown>(`/projects/${projectId}/glossary/entries`, { method: 'POST', body: JSON.stringify(body) })),
    update: async (projectId: string, entryId: string, patch: EntryPatch): Promise<StressEntry> =>
      stressEntrySchema.parse(
        await request<unknown>(`/projects/${projectId}/glossary/entries/${entryId}`, { method: 'PATCH', body: JSON.stringify(patch) })
      ),
    remove: (projectId: string, entryId: string) =>
      request<void>(`/projects/${projectId}/glossary/entries/${entryId}`, { method: 'DELETE' }),
    addForm: async (projectId: string, entryId: string, body: { form_key: string; variants: string[] }): Promise<StressEntry> =>
      stressEntrySchema.parse(
        await request<unknown>(`/projects/${projectId}/glossary/entries/${entryId}/forms`, { method: 'POST', body: JSON.stringify(body) })
      ),
    updateForm: async (
      projectId: string,
      entryId: string,
      formId: string,
      patch: { variants: string[] } | { status: 'approved' | 'rejected' }
    ): Promise<StressEntry> =>
      stressEntrySchema.parse(
        await request<unknown>(`/projects/${projectId}/glossary/entries/${entryId}/forms/${formId}`, {
          method: 'PATCH',
          body: JSON.stringify(patch),
        })
      ),
    /** Индекс ударений проекта для просмотрщика (fileId — файл, который открыт); etag — версия, что уже есть */
    stressIndex: async (projectId: string, fileId: string, etag: string | null): Promise<StressIndexFetch> => {
      const r = await requestConditional(`/projects/${projectId}/files/${fileId}/stress-index`, etag);
      if (r?.notModified) return { notModified: true };
      const parsed = stressIndexResponseSchema.parse(r?.body);
      return { notModified: false, etag: r?.etag ?? null, entries: parsed.entries };
    },
    /** Меню слова: кандидаты в формы слова из словника книги */
    /** seen — слово со страницы: оно в списке форм, даже если его нет в словнике книги */
    wordForms: async (projectId: string, word: string, seen?: string): Promise<WordForms> =>
      wordFormsSchema.parse(
        await request<unknown>(`/projects/${projectId}/glossary/word-forms?word=${encodeURIComponent(word)}${seen ? `&seen=${encodeURIComponent(seen)}` : ''}`)
      ),
    /** Карточка слова: примеры из книги по ключам слова и склонений (не больше 40) */
    contexts: async (projectId: string, keys: readonly string[]): Promise<Record<string, WordContext>> =>
      wordContextsSchema.parse(await request<unknown>(`/projects/${projectId}/glossary/contexts?keys=${encodeURIComponent(keys.join(','))}`)).contexts,
    hide: (projectId: string, wordKey: string) =>
      request<void>(`/projects/${projectId}/glossary/hidden`, { method: 'POST', body: JSON.stringify({ word_key: wordKey }) }),
    unhide: (projectId: string, wordKey: string) =>
      request<void>(`/projects/${projectId}/glossary/hidden/${encodeURIComponent(wordKey)}`, { method: 'DELETE' }),
    /** Файл для корректора: предложения в шаблоне .xlsx */
    correctorSheet: (projectId: string) => requestFile(`/projects/${projectId}/glossary/suggestions.xlsx`),
    /** Загрузка глоссария из файлов «Глоссарий» (glossaryImportModel.ts) */
    imports: {
      /** 202; идёт другая загрузка — ApiError 409 с import_id в теле */
      start: async (projectId: string, fileIds: string[]): Promise<GlossaryImport> =>
        importResponseSchema.parse(
          await request<unknown>(`/projects/${projectId}/glossary/imports`, { method: 'POST', body: JSON.stringify({ file_ids: fileIds }) })
        ).import,
      get: async (projectId: string, importId: string): Promise<GlossaryImport> =>
        importResponseSchema.parse(await request<unknown>(`/projects/${projectId}/glossary/imports/${importId}`)).import,
      /** «Добавить»: идемпотентно — повтор отвечает тем же результатом */
      apply: async (projectId: string, importId: string, decisions: ImportDecisions): Promise<ImportCounts> =>
        applyResponseSchema.parse(
          await request<unknown>(`/projects/${projectId}/glossary/imports/${importId}/apply`, { method: 'POST', body: JSON.stringify({ decisions }) })
        ).result,
      revert: (projectId: string, importId: string) =>
        request<void>(`/projects/${projectId}/glossary/imports/${importId}/applied`, { method: 'DELETE' }),
      /** «Отмена» в окне: неприменённую загрузку больше не предлагать */
      cancel: (projectId: string, importId: string) =>
        request<void>(`/projects/${projectId}/glossary/imports/${importId}`, { method: 'DELETE' }),
    },
  },
  projectFiles: {
    list: (projectId: string) => request<ProjectFile[]>(`/projects/${projectId}/files`),
    /** Новый дизайн: файлы и ревизия того же снимка — устаревший снимок клиент отбрасывает (lib/serverListSync) */
    listWithRevision: async (projectId: string): Promise<{ files: ProjectFile[]; revision: FilesRevision }> => {
      const r = await requestWithFilesRevision<ProjectFile[]>(`/projects/${projectId}/files`);
      return { files: r?.body ?? [], revision: r?.revision ?? null };
    },
    /** PDF для просмотра (сам PDF или PDF-копия Word); куки сессии, Range — грузит pdf.js */
    previewUrl: (projectId: string, fileId: string) => `${API_BASE_URL}/projects/${projectId}/files/${fileId}/preview`,
    /** Таблица для ридера таблиц; ответ проверяется схемой (sheetModel.ts) */
    sheet: async (projectId: string, fileId: string): Promise<SheetWorkbook> =>
      sheetWorkbookSchema.parse(await request<unknown>(`/projects/${projectId}/files/${fileId}/sheet`)),
    upload: async (
      projectId: string,
      file: File,
      uploadedBy?: string | null,
      /** Прогресс передачи файла клиент→сервер (0–25% финального прогресса) */
      onServerProgress?: (pct: number) => void,
      /** Тип файла проекта (опционально, те же значения что PATCH file_type) */
      fileType?: string | null,
      /** Клиентский расчёт АЛ и страниц (только PDF/DOCX); при ошибке не передаётся */
      clientMetrics?: { authorSheets: number; pages: number } | null
    ): Promise<ProjectFile> => {
      const path = `/projects/${projectId}/files`;
      const pending = pendingCreateBefore(path, false);
      // Файл в только что заведённый черновик: XHR идёт мимо request, ждём создания здесь
      if (pending) await pending;
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);
        if (uploadedBy) formData.append('uploaded_by', uploadedBy);
        if (fileType !== undefined && fileType !== null && fileType !== '') {
          formData.append('file_type', fileType);
        }
        if (clientMetrics) {
          formData.append('file_author_sheets', String(clientMetrics.authorSheets));
          formData.append('file_pages', String(clientMetrics.pages));
        }

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_BASE_URL}${path}`);
        xhr.withCredentials = true;

        // XHR upload.progress отслеживает передачу клиент→сервер (фаза 1: 0-25%)
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onServerProgress) {
            onServerProgress(Math.round((e.loaded / e.total) * 100));
          }
        });

        xhr.addEventListener('load', () => {
          void (async () => {
            if (await handleXhrAuthError(xhr)) return;
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                resolve(JSON.parse(xhr.responseText) as ProjectFile);
              } catch {
                reject(new Error('Некорректный ответ сервера'));
              }
            } else {
              try {
                const err = JSON.parse(xhr.responseText);
                reject(new Error(err.error || xhr.statusText));
              } catch {
                reject(new Error(xhr.statusText));
              }
            }
          })();
        });

        xhr.addEventListener('error', () => reject(new Error('Ошибка сети при загрузке файла')));
        xhr.addEventListener('abort', () => reject(new Error('Загрузка отменена')));

        xhr.send(formData);
      });
    },
    update: (
      projectId: string,
      fileId: string,
      data: { file_name?: string; file_type?: string | null }
    ) =>
      request<ProjectFile>(`/projects/${projectId}/files/${fileId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (projectId: string, fileId: string) =>
      request<void>(`/projects/${projectId}/files/${fileId}`, { method: 'DELETE' }),
    /** Новый дизайн: правка и ревизия, под которой сервер её записал */
    updateWithRevision: async (
      projectId: string,
      fileId: string,
      data: { file_name?: string; file_type?: string | null }
    ): Promise<{ file: ProjectFile; revision: FilesRevision }> => {
      const r = await requestWithFilesRevision<ProjectFile>(`/projects/${projectId}/files/${fileId}`, { method: 'PATCH', body: JSON.stringify(data) });
      return { file: r.body, revision: r.revision };
    },
    /** Новый дизайн: удаление и ревизия, под которой сервер его записал */
    deleteWithRevision: async (projectId: string, fileId: string): Promise<{ revision: FilesRevision }> => {
      const r = await requestWithFilesRevision<void>(`/projects/${projectId}/files/${fileId}`, { method: 'DELETE' });
      return { revision: r?.revision ?? null };
    },
  },
  readerFiles: {
    list: (personId: string) => request<ReaderFile[]>(`/readers/${personId}/files`),
    upload: (
      personId: string,
      file: File,
      uploadedBy?: string | null,
      onServerProgress?: (pct: number) => void
    ): Promise<ReaderFile> => {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);
        if (uploadedBy) formData.append('uploaded_by', uploadedBy);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_BASE_URL}/readers/${personId}/files`);
        xhr.withCredentials = true;

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onServerProgress) {
            onServerProgress(Math.round((e.loaded / e.total) * 100));
          }
        });

        xhr.addEventListener('load', () => {
          void (async () => {
            if (await handleXhrAuthError(xhr)) return;
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                resolve(JSON.parse(xhr.responseText) as ReaderFile);
              } catch {
                reject(new Error('Некорректный ответ сервера'));
              }
            } else {
              try {
                const err = JSON.parse(xhr.responseText);
                reject(new Error(err.error || xhr.statusText));
              } catch {
                reject(new Error(xhr.statusText));
              }
            }
          })();
        });

        xhr.addEventListener('error', () => reject(new Error('Ошибка сети при загрузке файла')));
        xhr.addEventListener('abort', () => reject(new Error('Загрузка отменена')));

        xhr.send(formData);
      });
    },
    update: (personId: string, fileId: string, data: { file_name: string }) =>
      request<ReaderFile>(`/readers/${personId}/files/${fileId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (personId: string, fileId: string) =>
      request<void>(`/readers/${personId}/files/${fileId}`, { method: 'DELETE' }),
  },
  chat: {
    models: () => request<AgentChatModelOption[]>('/chat/models'),
    quota: () => request<AgentChatQuotaSnapshot>('/chat/quota'),
  },
  emails: {
    draft: (data: {
      kind: EmailKind;
      project_id: string;
      recipient_person_ids?: string[];
      review_check_type?: string | null;
      review_deadline?: string | null;
      modal_project?:
        | ReviewCheckModalProjectPayload
        | PreArchiveUploadModalProjectPayload
        | MountingCompleteModalProjectPayload
        | CorrectionsAppliedModalProjectPayload
        | ProjectReviewedModalProjectPayload;
    }) =>
      request<EmailDraftResponse>('/emails/draft', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    send: (data: EmailSendBody) =>
      request<EmailSendResponse>('/emails/send', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },
  messenger: {
    getThreads: () =>
      request<{ threads: MessengerThread[]; total_unread: number }>('/messenger/threads'),
    getUnreadTotal: () =>
      request<{ total_unread: number }>('/messenger/unread-total'),
    getMessages: async (threadId: string, params?: { limit?: number; before?: string; after?: string }) => {
      const p = new URLSearchParams();
      if (params?.limit != null) p.set('limit', String(params.limit));
      if (params?.before?.trim()) p.set('before', params.before.trim());
      if (params?.after?.trim()) p.set('after', params.after.trim());
      const q = p.toString();
      const raw = await request<MessengerMessagesListResponseRaw>(
        `/messenger/threads/${threadId}/messages${q ? `?${q}` : ''}`,
        { cache: 'no-store' }
      );
      return normalizeMessengerMessagesListResponse(raw);
    },
    searchMessages: (q: string, params?: { limit?: number }) => {
      const p = new URLSearchParams();
      if (q.trim()) p.set('q', q.trim());
      if (params?.limit != null) p.set('limit', String(params.limit));
      const query = p.toString();
      return request<{ results: MessengerMessageSearchResult[] }>(
        `/messenger/messages/search${query ? `?${query}` : ''}`
      );
    },
    getMessagesContext: (
      threadId: string,
      params: { messageId: string; before?: number; after?: number }
    ) => {
      const p = new URLSearchParams();
      p.set('messageId', params.messageId);
      if (params.before != null) p.set('before', String(params.before));
      if (params.after != null) p.set('after', String(params.after));
      return request<{
        messages: MessengerMessage[];
        hasMore: boolean;
        last_read_message_id: string | null;
      }>(`/messenger/threads/${threadId}/messages/context?${p.toString()}`);
    },
    getPinnedMessages: (threadId: string) =>
      request<{ pins: MessengerPinnedMessage[] }>(
        `/messenger/threads/${encodeURIComponent(threadId)}/pins`
      ),
    pinMessage: (threadId: string, msgId: string) =>
      request<{ ok: boolean; pins: MessengerPinnedMessage[] }>(
        `/messenger/threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(msgId)}/pin`,
        { method: 'POST' }
      ),
    unpinMessage: (threadId: string, msgId: string) =>
      request<{ ok: boolean; pins: MessengerPinnedMessage[] }>(
        `/messenger/threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(msgId)}/pin`,
        { method: 'DELETE' }
      ),
    createThreadWithPeer: (peerId: string) =>
      request<MessengerThread>('/messenger/threads/with/' + encodeURIComponent(peerId), {
        method: 'POST',
      }),
    getSavedThread: () =>
      request<MessengerThread>('/messenger/threads/saved', { method: 'POST' }),
    createGroupThread: (data: { title: string; member_ids: string[] }) =>
      request<MessengerThread>('/messenger/threads/group', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    getThreadMembers: (threadId: string) =>
      request<{ members: MessengerThreadMemberRow[] }>(
        `/messenger/threads/${encodeURIComponent(threadId)}/members`
      ),
    addThreadMember: (threadId: string, personId: string) =>
      request<{ ok: boolean }>(`/messenger/threads/${encodeURIComponent(threadId)}/members`, {
        method: 'POST',
        body: JSON.stringify({ person_id: personId }),
      }),
    leaveThread: (threadId: string) =>
      request<{ ok: boolean; thread_deleted?: boolean }>(
        `/messenger/threads/${encodeURIComponent(threadId)}/leave`,
        { method: 'POST', body: JSON.stringify({}) }
      ),
    removeThreadMember: (threadId: string, personId: string) =>
      request<{ ok: boolean }>(
        `/messenger/threads/${encodeURIComponent(threadId)}/members/${encodeURIComponent(personId)}`,
        { method: 'DELETE' }
      ),
    deleteGroupThread: (threadId: string) =>
      request<{ ok: boolean }>(`/messenger/threads/${encodeURIComponent(threadId)}`, { method: 'DELETE' }),
    deleteMessage: (threadId: string, msgId: string) =>
      request<{ ok: boolean }>(`/messenger/threads/${threadId}/messages/${msgId}/delete`, {
        method: 'PATCH',
      }),
    editMessage: (threadId: string, msgId: string, body: string) =>
      request<MessengerMessage>(`/messenger/threads/${threadId}/messages/${msgId}`, {
        method: 'PATCH',
        body: JSON.stringify({ body }),
      }),
    getMessageLikeLikers: (threadId: string, msgId: string) =>
      request<{ likers: MessengerLikeLikerPreview[] }>(
        `/messenger/threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(msgId)}/reactions/like`
      ),
    getMessageReadReceipts: (threadId: string, msgId: string) =>
      request<{ readers: MessengerMessageReadReceiptRow[] }>(
        `/messenger/threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(msgId)}/read-receipts`
      ),
    setMessageLike: (threadId: string, msgId: string) =>
      request<{
        ok: boolean;
        like_count: number;
        liked_by_me: boolean;
        like_likers_preview: MessengerLikeLikerPreview[];
      }>(
        `/messenger/threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(msgId)}/reactions/like`,
        { method: 'POST' }
      ),
    removeMessageLike: (threadId: string, msgId: string) =>
      request<{
        ok: boolean;
        like_count: number;
        liked_by_me: boolean;
        like_likers_preview: MessengerLikeLikerPreview[];
      }>(
        `/messenger/threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(msgId)}/reactions/like`,
        { method: 'DELETE' }
      ),
    sendMessage: (
      threadId: string,
      data: {
        body?: string;
        file_url?: string;
        file_name?: string;
        file_size?: number;
        mime_type?: string;
        file_key?: string;
        image_width?: number;
        image_height?: number;
        context_attachments?: unknown;
        reply_to_id?: string;
        forwarded_from_message_id?: string;
        forwarded_from_label?: string;
      }
    ) =>
      request<MessengerMessage>(`/messenger/threads/${threadId}/messages`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    /** Одна транзакция БД: своё сообщение (опционально) + пересланные */
    sendMessagesAtomicBatch: (
      threadId: string,
      data: {
        own?: Record<string, unknown>;
        forwards: Record<string, unknown>[];
      }
    ) =>
      request<{ messages: MessengerMessage[] }>(`/messenger/threads/${threadId}/messages/batch`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    markRead: (threadId: string, lastReadMessageId: string | null) =>
      request<{ ok: boolean }>(`/messenger/threads/${threadId}/read`, {
        method: 'POST',
        body: JSON.stringify({ last_read_message_id: lastReadMessageId }),
      }),
    getDrafts: () => request<{ drafts: MessengerComposerDraft[] }>('/messenger/drafts'),
    putDraft: (
      targetKind: MessengerDraftTargetKind,
      targetId: string,
      body: {
        text: string;
        reply_to_message_id: string | null;
        forward_message_ids: string[];
        client_updated_at: string;
      }
    ) =>
      request<{ draft: MessengerComposerDraft | null; stale?: boolean }>(
        `/messenger/drafts/${encodeURIComponent(targetKind)}/${encodeURIComponent(targetId)}`,
        { method: 'PUT', body: JSON.stringify(body) }
      ),
    deleteDraft: (
      targetKind: MessengerDraftTargetKind,
      targetId: string,
      clientUpdatedAt: string
    ) =>
      request<{ draft: MessengerComposerDraft | null; stale?: boolean }>(
        `/messenger/drafts/${encodeURIComponent(targetKind)}/${encodeURIComponent(targetId)}`,
        {
          method: 'DELETE',
          body: JSON.stringify({ client_updated_at: clientUpdatedAt }),
        }
      ),
    uploadFile: (file: File): Promise<MessengerUploadResult> => {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_BASE_URL}/messenger/upload`);
        xhr.withCredentials = true;
        xhr.addEventListener('load', () => {
          void (async () => {
            if (await handleXhrAuthError(xhr)) return;
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                resolve(JSON.parse(xhr.responseText) as MessengerUploadResult);
              } catch {
                reject(new Error('Некорректный ответ сервера'));
              }
            } else {
              try {
                const err = JSON.parse(xhr.responseText);
                reject(new Error(err.error || xhr.statusText));
              } catch {
                reject(new Error(xhr.statusText));
              }
            }
          })();
        });
        xhr.addEventListener('error', () => reject(new Error('Ошибка сети при загрузке файла')));
        xhr.addEventListener('abort', () => reject(new Error('Загрузка отменена')));
        xhr.send(formData);
      });
    },
  },
  push: {
    getVapidPublicKey: () => request<{ publicKey: string }>('/push/vapid-public-key'),
    subscribe: (
      subscription: PushSubscriptionJSON,
      opts?: { channel?: 'web_push' | 'fcm' | 'apns'; device_token?: string }
    ) =>
      request<void>('/push/subscribe', {
        method: 'POST',
        body: JSON.stringify({
          subscription,
          channel: opts?.channel ?? 'web_push',
          ...(opts?.device_token ? { device_token: opts.device_token } : {}),
        }),
      }),
    unsubscribe: (endpoint: string) =>
      request<void>('/push/unsubscribe', {
        method: 'POST',
        body: JSON.stringify({ endpoint }),
      }),
  },
  planner: {
    getSummary: () => request<{ text: string }>('/planner/summary'),
    getProjectAnalysis: (projectId: string) =>
      request<{
        projectId: string;
        projectName: string;
        status: string;
        message: string;
        remainSessions: number | null;
        plannedSessionsCount: number;
        sessionsPerWeek: number;
      }>(`/planner/project/${projectId}`),
  },
  agentChats: {
    list: (params?: { limit?: number; cursor_iso?: string | null; cursor_id?: string | null }) => {
      const search = new URLSearchParams();
      if (params?.limit != null) search.set('limit', String(params.limit));
      if (params?.cursor_iso?.trim()) search.set('cursor_iso', params.cursor_iso.trim());
      if (params?.cursor_id?.trim()) search.set('cursor_id', params.cursor_id.trim());
      const q = search.toString();
      return request<AgentChatsListResponse>(`/agent-chats${q ? `?${q}` : ''}`);
    },
    create: (body?: { title?: string }) =>
      request<AgentChatListItem>('/agent-chats', {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }),
    messages: (
      chatId: string,
      params?: { limit?: number; before_iso?: string | null; before_id?: string | null }
    ) => {
      const search = new URLSearchParams();
      if (params?.limit != null) search.set('limit', String(params.limit));
      if (params?.before_iso?.trim()) search.set('before_iso', params.before_iso.trim());
      if (params?.before_id?.trim()) search.set('before_id', params.before_id.trim());
      const q = search.toString();
      return request<AgentChatMessagesPageResponse>(`/agent-chats/${chatId}/messages${q ? `?${q}` : ''}`);
    },
    read: (chatId: string, lastReadMessageId: string) =>
      request<{ ok: true }>(`/agent-chats/${encodeURIComponent(chatId)}/read`, {
        method: 'POST',
        body: JSON.stringify({ last_read_message_id: lastReadMessageId }),
      }),
    delete: (chatId: string) => request<void>(`/agent-chats/${chatId}`, { method: 'DELETE' }),
    patch: (chatId: string, body: { title: string }) =>
      request<AgentChatTitlePatchResponse>(`/agent-chats/${encodeURIComponent(chatId)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    truncateAfter: (chatId: string, uiMessageId: string) =>
      request<void>(`/agent-chats/${encodeURIComponent(chatId)}/truncate-after`, {
        method: 'POST',
        body: JSON.stringify({ ui_message_id: uiMessageId }),
      }),
    upload: (chatId: string, file: File): Promise<AgentChatUploadResult> =>
      new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_BASE_URL}/agent-chats/${encodeURIComponent(chatId)}/uploads`);
        xhr.withCredentials = true;
        xhr.addEventListener('load', () => {
          void (async () => {
            if (await handleXhrAuthError(xhr)) return;
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                resolve(JSON.parse(xhr.responseText) as AgentChatUploadResult);
              } catch {
                reject(new Error('Некорректный ответ сервера'));
              }
            } else {
              try {
                const err = JSON.parse(xhr.responseText);
                reject(new Error(err.error || xhr.statusText));
              } catch {
                reject(new Error(xhr.statusText));
              }
            }
          })();
        });
        xhr.addEventListener('error', () => reject(new Error('Ошибка сети при загрузке файла')));
        xhr.addEventListener('abort', () => reject(new Error('Загрузка отменена')));
        xhr.send(formData);
      }),
  },
  authors: {
    list: (q?: string, opts?: { limit?: number; offset?: number }) => {
      const search = new URLSearchParams();
      if (q?.trim()) search.set('q', q.trim());
      if (opts?.limit != null) search.set('limit', String(opts.limit));
      if (opts?.offset != null) search.set('offset', String(opts.offset));
      const query = search.toString();
      return request<{ items: Author[]; hasMore: boolean }>(`/authors${query ? `?${query}` : ''}`);
    },
    create: (data: { person_id?: string; first_name?: string; last_name?: string }) =>
      request<Author>('/authors', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    delete: (personId: string) => request<void>(`/authors/${personId}`, { method: 'DELETE' }),
  },
  legalEntities: {
    list: (q?: string, opts?: { limit?: number; offset?: number }) => {
      const search = new URLSearchParams();
      if (q?.trim()) search.set('q', q.trim());
      if (opts?.limit != null) search.set('limit', String(opts.limit));
      if (opts?.offset != null) search.set('offset', String(opts.offset));
      const query = search.toString();
      return request<{ items: LegalEntity[]; hasMore: boolean }>(
        `/legal-entities${query ? `?${query}` : ''}`
      );
    },
    get: (id: string) => request<LegalEntity>(`/legal-entities/${id}`),
    create: (data: { name: string; notes?: string | null }) =>
      request<LegalEntity>('/legal-entities', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: { name?: string; notes?: string | null }) =>
      request<LegalEntity>(`/legal-entities/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    contracts: (id: string) => request<LegalEntityContract[]>(`/legal-entities/${id}/contracts`),
  },
  studios: {
    list: (q?: string, opts?: { limit?: number; offset?: number }) => {
      const search = new URLSearchParams();
      if (q?.trim()) search.set('q', q.trim());
      if (opts?.limit != null) search.set('limit', String(opts.limit));
      if (opts?.offset != null) search.set('offset', String(opts.offset));
      const query = search.toString();
      return request<{ items: Studio[]; hasMore: boolean }>(`/studios${query ? `?${query}` : ''}`);
    },
    get: (id: string) => request<Studio>(`/studios/${id}`),
    create: (data: { name: string; legal_entity_id?: string | null; notes?: string | null }) =>
      request<Studio>('/studios', { method: 'POST', body: JSON.stringify(data) }),
    update: (
      id: string,
      data: { name?: string; legal_entity_id?: string | null; notes?: string | null }
    ) => request<Studio>(`/studios/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  },
  /** @deprecated use legalEntities / studios */
  contractors: {
    list: (
      q?: string,
      opts?: { limit?: number; offset?: number; type?: 'studio' | 'solo' }
    ) => {
      if (opts?.type === 'studio') {
        const search = new URLSearchParams();
        if (q?.trim()) search.set('q', q.trim());
        if (opts?.limit != null) search.set('limit', String(opts.limit));
        if (opts?.offset != null) search.set('offset', String(opts.offset));
        const query = search.toString();
        return request<{ items: Studio[]; hasMore: boolean }>(
          `/studios${query ? `?${query}` : ''}`
        ).then((res) => ({
          items: res.items.map(
            (s) =>
              ({
                id: s.id,
                name: s.name,
                legal_entity: s.legal_entity?.name ?? null,
                type: 'studio' as const,
              }) satisfies Contractor
          ),
          hasMore: res.hasMore,
        }));
      }
      const search = new URLSearchParams();
      if (q?.trim()) search.set('q', q.trim());
      if (opts?.limit != null) search.set('limit', String(opts.limit));
      if (opts?.offset != null) search.set('offset', String(opts.offset));
      const query = search.toString();
      return request<{ items: LegalEntity[]; hasMore: boolean }>(
        `/legal-entities${query ? `?${query}` : ''}`
      ).then((res) => ({
        items: res.items.map(
          (le) =>
            ({
              id: le.id,
              name: le.name,
              legal_entity: le.name,
              type: 'solo' as const,
            }) satisfies Contractor
        ),
        hasMore: res.hasMore,
      }));
    },
    services: (id: string) =>
      request<LegalEntityContract[]>(`/legal-entities/${id}/contracts`).then((rows) =>
        rows.map(
          (r) =>
            ({
              id: r.id,
              service: r.name,
              service_price: r.price,
            }) satisfies ContractorService
        )
      ),
  },
  producers: {
    list: (q?: string, opts?: { limit?: number; offset?: number }) => {
      const search = new URLSearchParams();
      if (q?.trim()) search.set('q', q.trim());
      if (opts?.limit != null) search.set('limit', String(opts.limit));
      if (opts?.offset != null) search.set('offset', String(opts.offset));
      const query = search.toString();
      return request<{ items: Producer[]; hasMore: boolean }>(`/producers${query ? `?${query}` : ''}`);
    },
    create: (data: { person_id: string }) => request<Producer>('/producers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    update: (personId: string, data: { dismissed: boolean }) => request<Producer>(`/producers/${personId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    delete: (personId: string) => request<void>(`/producers/${personId}`, { method: 'DELETE' }),
  },
  departments: {
    list: (q?: string, opts?: { limit?: number; offset?: number }) => {
      const search = new URLSearchParams();
      if (q?.trim()) search.set('q', q.trim());
      if (opts?.limit != null) search.set('limit', String(opts.limit));
      if (opts?.offset != null) search.set('offset', String(opts.offset));
      const query = search.toString();
      return request<{ items: Department[]; hasMore: boolean }>(`/departments${query ? `?${query}` : ''}`);
    },
    create: (data: { code: string }) => request<Department>('/departments', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    update: (id: string, data: { code: string }) => request<Department>(`/departments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    delete: (id: string) => request<void>(`/departments/${id}`, { method: 'DELETE' }),
  },
  editions: {
    list: (q?: string, departmentId?: string | null, opts?: { limit?: number; offset?: number }) => {
      const p = new URLSearchParams();
      if (q?.trim()) p.set('q', q.trim());
      if (departmentId?.trim()) p.set('department_id', departmentId.trim());
      if (opts?.limit != null) p.set('limit', String(opts.limit));
      if (opts?.offset != null) p.set('offset', String(opts.offset));
      const params = p.toString();
      return request<{ items: Edition[]; hasMore: boolean }>(`/editions${params ? `?${params}` : ''}`);
    },
    create: (data: { name: string; department_id: string }) => request<Edition>('/editions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    update: (id: string, data: { name: string; department_id: string }) => request<Edition>(`/editions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    delete: (id: string) => request<void>(`/editions/${id}`, { method: 'DELETE' }),
  },
  editors: {
    list: (q?: string, opts?: { limit?: number; offset?: number; department_id?: string | null }) => {
      const search = new URLSearchParams();
      if (q?.trim()) search.set('q', q.trim());
      if (opts?.department_id?.trim()) search.set('department_id', opts.department_id.trim());
      if (opts?.limit != null) search.set('limit', String(opts.limit));
      if (opts?.offset != null) search.set('offset', String(opts.offset));
      const query = search.toString();
      return request<{ items: Editor[]; hasMore: boolean }>(`/editors${query ? `?${query}` : ''}`);
    },
    create: (data: { person_id?: string; edition_id: string; first_name?: string; last_name?: string }) => request<Editor>('/editors', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    update: (personId: string, data: { dismissed: boolean }) => request<Editor>(`/editors/${personId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    delete: (personId: string) => request<void>(`/editors/${personId}`, { method: 'DELETE' }),
  },
  soundengineers: {
    list: (q?: string, opts?: { limit?: number; offset?: number }) => {
      const search = new URLSearchParams();
      if (q?.trim()) search.set('q', q.trim());
      if (opts?.limit != null) search.set('limit', String(opts.limit));
      if (opts?.offset != null) search.set('offset', String(opts.offset));
      const query = search.toString();
      return request<{ items: SoundEngineer[]; hasMore: boolean }>(`/soundengineers${query ? `?${query}` : ''}`);
    },
    create: (data: { person_id: string }) => request<SoundEngineer>('/soundengineers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    update: (personId: string, data: { dismissed?: boolean; color?: string | null }) => request<SoundEngineer>(`/soundengineers/${personId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    delete: (personId: string) => request<void>(`/soundengineers/${personId}`, { method: 'DELETE' }),
  },
  techeditors: {
    list: (q?: string, opts?: { limit?: number; offset?: number }) => {
      const search = new URLSearchParams();
      if (q?.trim()) search.set('q', q.trim());
      if (opts?.limit != null) search.set('limit', String(opts.limit));
      if (opts?.offset != null) search.set('offset', String(opts.offset));
      const query = search.toString();
      return request<{ items: Techeditor[]; hasMore: boolean }>(`/techeditors${query ? `?${query}` : ''}`);
    },
    create: (data: { person_id: string }) => request<Techeditor>('/techeditors', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    delete: (personId: string) => request<void>(`/techeditors/${personId}`, { method: 'DELETE' }),
  },
  correctors: {
    list: (q?: string, opts?: { limit?: number; offset?: number }) => {
      const search = new URLSearchParams();
      if (q?.trim()) search.set('q', q.trim());
      if (opts?.limit != null) search.set('limit', String(opts.limit));
      if (opts?.offset != null) search.set('offset', String(opts.offset));
      const query = search.toString();
      return request<{ items: Corrector[]; hasMore: boolean }>(`/correctors${query ? `?${query}` : ''}`);
    },
    create: (data: { person_id: string }) => request<Corrector>('/correctors', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    delete: (personId: string) => request<void>(`/correctors/${personId}`, { method: 'DELETE' }),
  },
  fonoteka: {
    listPacks: () => request<FonotekaPack[]>('/fonoteka/packs'),
    /** Проекты Фонотеки порциями, без треков (окно выбора в карточке); projectId — его проекты Фонотеки сверху */
    packSummaries: (params: { projectId?: string; q?: string; offset?: number }) => {
      const search = new URLSearchParams();
      if (params.projectId) search.set('projectId', params.projectId);
      if (params.q?.trim()) search.set('q', params.q.trim());
      if (params.offset) search.set('offset', String(params.offset));
      const query = search.toString();
      return request<FonotekaPackSummaryPage>(`/fonoteka/pack-summaries${query ? `?${query}` : ''}`);
    },
    /** Треки одного проекта Фонотеки — когда его раскрыли */
    packTracks: (packId: string) => request<FonotekaTrackLite[]>(`/fonoteka/packs/${packId}/tracks`),
    /** «Новый проект» в Фонотеке: место для существующего проекта трекера; уже есть — 409 */
    createPack: (data: { project_id: string }) =>
      request<FonotekaPack>('/fonoteka/packs', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    patchPack: (id: string, data: { name?: string; display_order?: number }) =>
      request<FonotekaPack>(`/fonoteka/packs/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    deletePack: (id: string) => request<void>(`/fonoteka/packs/${id}`, { method: 'DELETE' }),
    uploadTrack: (packId: string, file: File, onProgress?: (pct: number) => void): Promise<FonotekaTrack> =>
      uploadAudioFile<FonotekaTrack>(`/fonoteka/packs/${packId}/tracks`, file, onProgress),
    patchTrack: (
      id: string,
      data: { file_name?: string; display_order?: number; legal_entity_id?: string | null }
    ) =>
      request<FonotekaTrack>(`/fonoteka/tracks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    deleteTrack: (id: string) => request<void>(`/fonoteka/tracks/${id}`, { method: 'DELETE' }),
    putTrackTags: (id: string, tag_ids: string[]) =>
      request<FonotekaTrack>(`/fonoteka/tracks/${id}/tags`, {
        method: 'PUT',
        body: JSON.stringify({ tag_ids }),
      }),
  },
  access: {
    me: () =>
      request<{
        capabilities: AccessCapabilities;
        preview: AccessPreview | null;
        group_slug: string | null;
        access: string | null;
      }>('/access/me'),
    previewExit: () => request<{ ok: true }>('/access/preview/exit', { method: 'POST' }),
  },
};

export type { AccessCapabilities, AccessPreview } from '@/types/access';

export interface Person {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  email?: string | null;
  phone?: string | null;
}

export interface PersonSearchResult extends Omit<Person, 'first_name' | 'last_name'> {
  first_name?: string;
  last_name?: string;
  is_reader?: boolean;
  /** @deprecated use studio / legal_entity */
  contractor?: { id: string; legal_entity: string; name?: string | null; type?: string } | null;
  /** @deprecated use studio (0..1) */
  studios?: ReaderStudio[];
  studio?: Studio | null;
  legal_entity?: LegalEntity | null;
  links?: ReaderLink[];
  reader_prices?: ReaderCatalogPrice[];
  tags?: ReaderTag[];
  name_note?: string | null;
  cooperation_state?: string | null;
}

export interface ReaderTag {
  id: string;
  name: string;
  color: string;
  tag_group: string;
  display_order: number;
  created_at: string;
}

/** Справочник тегов фонотеки (та же форма, что reader_tags). */
export type MusicTag = ReaderTag;

export type FonotekaMusicTag = {
  id: string;
  name: string;
  color: string;
  tag_group: string;
  display_order: number;
};

export type FonotekaUsedProject = { id: string; name: string };

export type FonotekaTrack = {
  id: string;
  pack_id: string;
  file_name: string;
  file_url: string;
  file_size: number | null;
  /** Секунды по заголовку файла; null — формат без поддержки или не прочитался */
  duration_sec: number | null;
  uploaded_at: string;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  display_order: number;
  legal_entity_id: string | null;
  legal_entity_name: string | null;
  tags: FonotekaMusicTag[];
  used_in_projects: FonotekaUsedProject[];
};

export type FonotekaPack = {
  id: string;
  /** У проекта трекера — его имя (последнее известное, если проект удалён) */
  name: string;
  /** Проект трекера; null — проект Фонотеки без проекта трекера (остался от прежних «пакетов»), его можно переименовать */
  project_id: string | null;
  display_order: number;
  created_at: string;
  tracks: FonotekaTrack[];
};

/** Трек без тегов, автора и «где используется» (server/src/fonoteka/types.ts) */
export type FonotekaTrackLite = {
  id: string;
  pack_id: string;
  file_name: string;
  file_url: string;
  uploaded_at: string;
  uploaded_by_name: string | null;
  duration_sec: number | null;
};

/** Проект Фонотеки в окне выбора: без треков, только их число */
export type FonotekaPackSummary = {
  id: string;
  name: string;
  project_id: string | null;
  track_count: number;
  /** Есть треки проекта, для которого открыто окно */
  used_in_project: boolean;
  /** При поиске — найденные треки (совпало название проекта Фонотеки — все); без поиска — null */
  matched_tracks: FonotekaTrackLite[] | null;
};

export type FonotekaPackSummaryPage = { items: FonotekaPackSummary[]; hasMore: boolean };

/** Трек проекта трекера с его проектом Фонотеки */
export type ProjectFonotekaTrack = FonotekaTrackLite & { pack_name: string; pack_project_id: string | null };

export type ProjectFonotekaTracks = { track_ids: string[]; tracks: ProjectFonotekaTrack[] };

export type MiniAppType = 'game' | 'utility';

export interface MiniAppCatalogRow {
  id: string;
  name: string;
  description: string;
  type: MiniAppType;
  preview_link: string;
  likes_count: number;
  liked_by_me: boolean;
}

export interface ReaderStudio {
  id: string;
  legal_entity?: string | null;
  name?: string | null;
}

export interface ReaderLinkInput {
  link: string;
  link_name?: string | null;
  display_order?: number;
}

export interface ReaderLink {
  id: string;
  reader_person_id: string;
  link: string;
  link_name: string | null;
  display_order: number;
  created_at: string;
}

export interface ReaderCatalogPrice {
  id: string;
  reader_person_id: string;
  price_name: string | null;
  price: number | string;
  display_order: number;
  created_at: string;
}

export interface ReaderPriceInput {
  id?: string;
  price_name?: string | null;
  price: number;
  display_order?: number;
}

/** Сводка ролей персоны при удалении из каталога чтецов (имя + другие каталожные роли, не «чтец»). */
export interface ReaderDeletionContext {
  name: string;
  otherRoles: string[];
}

export interface Reader {
  id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  email?: string | null;
  phone?: string | null;
  studio?: Studio | null;
  legal_entity?: LegalEntity | null;
  /** @deprecated use studio / legal_entity */
  contractor?: { id: string; legal_entity: string; name?: string | null; type?: string } | null;
  /** @deprecated use studio (0..1) */
  studios?: ReaderStudio[];
  links?: ReaderLink[];
  /** Позиции стоимости в каталоге (reader_prices) */
  reader_prices?: ReaderCatalogPrice[];
  /** Теги каталога (reader_tags через reader_tag_assignments) */
  tags?: ReaderTag[];
  /** Заметка к имени в каталоге чтецов (readers.name_note), до 40 символов */
  name_note?: string | null;
  /** Сотрудничество в каталоге (readers.cooperation_state) */
  cooperation_state?: string | null;
}

/** Сводка заметок персоны для строки каталога чтецов (батч). */
export interface ReaderPersonNotesSummary {
  noteCount: number;
  recentNoteCount: number;
}

/** Сводка файлов чтеца для строки каталога (батч). */
export interface ReaderFilesSummary {
  fileCount: number;
  recentFileCount: number;
}

export interface User {
  id: string;
  name?: string | null;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
  access?: string | null;
  is_active?: boolean;
  created_at?: string;
  last_seen_at?: string | null;
}

export type PersonCatalogStaffRole = 'editor' | 'producer' | 'soundengineer' | 'techeditor' | 'corrector';

export interface PersonLookupResponse {
  found: boolean;
  person?: Person;
}

export interface PersonRoleSnapshot {
  person: Person;
  reader: Reader | null;
  has_author: boolean;
  user: User | null;
  staff: { role: PersonCatalogStaffRole; edition_id?: string; dismissed?: boolean } | null;
}

/** Тело POST /persons/catalog-save (каталожные поля чтеца; проектные поля — отдельными PATCH). */
export interface PersonCatalogSaveRequest {
  person_id: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  is_reader: boolean;
  /** Каталог чтецов: вид сотрудничества (если is_reader); иначе игнорируется */
  cooperation_state?: string | null;
  reader_prices: ReaderPriceInput[];
  reader_links: Array<{ link: string; link_name: string; display_order: number }>;
  studio_id: string | null;
  legal_entity_id: string | null;
  /** Полная замена тегов чтеца (UUID reader_tags); не передавать — без изменений */
  reader_tag_ids?: string[];
  is_author: boolean;
  is_employee: boolean;
  staff_role: PersonCatalogStaffRole | '';
  edition_id: string;
  is_user: boolean;
  user_role: string;
  user_access: string;
  is_active?: boolean;
  staff_dismissed?: boolean;
}

export interface PersonCatalogSaveResponse {
  person: Person;
  created: {
    authors: Author[];
    readers: Reader[];
    editor: Editor | null;
  };
}

export type MessengerDraftTargetKind = 'thread' | 'agent_chat';

export type MessengerComposerDraft = {
  target_kind: MessengerDraftTargetKind;
  target_id: string;
  text: string;
  reply_to_message_id: string | null;
  forward_message_ids: string[];
  updated_at: string;
};

export interface MessengerThread {
  id: string;
  thread_kind: 'direct' | 'group';
  title?: string | null;
  last_message_at?: string | null;
  last_message_preview?: string | null;
  /** group: автор последнего сообщения (превью в сайдбаре: «Имя: …» / «Вы: …») */
  last_message_sender_id?: string | null;
  last_message_sender_name?: string | null;
  unread_count: number;
  /** direct only */
  peer_id?: string | null;
  peer_name?: string | null;
  /** group only (future) */
  member_ids?: string[];
  /** Тред "Сохраненные сообщения" */
  is_saved?: boolean;
  /** Роль текущего пользователя в треде (с сервера в GET /threads) */
  my_member_role?: 'admin' | 'member';
}

export interface MessengerMessageSearchResult {
  thread_id: string;
  thread_kind: 'direct' | 'group';
  title?: string | null;
  peer_id?: string | null;
  peer_name?: string | null;
  is_saved?: boolean;
  message: MessengerMessage;
}

export interface MessengerThreadMemberRow {
  person_id: string;
  display_name: string;
  member_role: 'admin' | 'member';
  joined_at: string;
}

/** Превью лайкнувших (до 3 человек, с сервера; avatar_url пока заглушка null). */
export interface MessengerLikeLikerPreview {
  person_id: string;
  first_name?: string | null;
  last_name?: string | null;
  avatar_url?: string | null;
}

/** Участник группы, отметивший сообщение прочитанным */
export interface MessengerMessageReadReceiptRow {
  person_id: string;
  display_name: string;
  read_at: string;
}

export interface MessengerPinnedMessage {
  message: MessengerMessage;
  pinned_by: string;
  pinned_by_name: string;
  pinned_at: string;
}

export type MessengerMessagesPage = {
  hasMoreOlder: boolean;
  hasMoreNewer: boolean;
};

export type MessengerMessagesRange = {
  oldestLoadedMessageId: string | null;
  newestLoadedMessageId: string | null;
};

export type MessengerMessagesListResponse = {
  messages: MessengerMessage[];
  page: MessengerMessagesPage;
  range: MessengerMessagesRange;
  /** Обратная совместимость: для older/initial = page.hasMoreOlder */
  hasMore: boolean;
  last_read_message_id: string | null;
};

type MessengerMessagesListResponseRaw = {
  messages: MessengerMessage[];
  page?: MessengerMessagesPage;
  range?: MessengerMessagesRange;
  hasMore?: boolean;
  last_read_message_id: string | null;
};

function normalizeMessengerMessagesListResponse(
  raw: MessengerMessagesListResponseRaw | undefined
): MessengerMessagesListResponse {
  if (!raw) {
    return {
      messages: [],
      page: { hasMoreOlder: false, hasMoreNewer: false },
      range: { oldestLoadedMessageId: null, newestLoadedMessageId: null },
      hasMore: false,
      last_read_message_id: null,
    };
  }
  const messages = Array.isArray(raw.messages) ? raw.messages : [];
  const page = raw.page ?? {
    hasMoreOlder: Boolean(raw.hasMore),
    hasMoreNewer: false,
  };
  const range = raw.range ?? {
    oldestLoadedMessageId: messages[0]?.id ?? null,
    newestLoadedMessageId: messages[messages.length - 1]?.id ?? null,
  };
  return {
    messages,
    page,
    range,
    hasMore: raw.hasMore ?? page.hasMoreOlder,
    last_read_message_id: raw.last_read_message_id ?? null,
  };
}

export interface MessengerReplySnapshot {
  id: string;
  sender_name: string;
  body: string;
  file_name?: string | null;
  file_url?: string | null;
  mime_type?: string | null;
  context_attachments?: unknown;
  /** Если ответ на пересланное — имя автора оригинала (для строки «→ …» в цитате). */
  forwarded_from_sender_name?: string | null;
}

export interface MessengerMessage {
  id: string;
  thread_id: string;
  sender_id: string;
  /** Сервисная строка группы (создание, участники) — отдельный UI */
  message_kind?: 'user' | 'system';
  /** Имя отправителя (с сервера), для групповых чатов */
  sender_display_name?: string | null;
  body: string;
  file_url?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  mime_type?: string | null;
  /** Пиксели оригинала для слота превью до загрузки картинки */
  image_width?: number | null;
  image_height?: number | null;
  context_attachments?: unknown;
  created_at: string;
  read_at?: string | null;
  /** Доставлено ≥1 устройству получателя (WhatsApp-style) */
  delivered_at?: string | null;
  /** Soft-delete: если задан — сообщение удалено */
  deleted_at?: string | null;
  /** Если задан — сообщение было отредактировано */
  edited_at?: string | null;
  /** Ответ на сообщение */
  reply_to_id?: string | null;
  reply_to?: MessengerReplySnapshot | null;
  /** Пересланное сообщение */
  forwarded_from_message_id?: string | null;
  /** Подпись пересылки без id в chat_messages (ассистент и т.п.) */
  forwarded_from_label?: string | null;
  forwarded_from_sender_name?: string | null;
  /** Лайки (несколько пользователей — счётчик; свой лайк — liked_by_me) */
  like_count?: number;
  liked_by_me?: boolean;
  is_pinned?: boolean;
  /** До 3 лайкнувших (последние по времени), для аватарок в бейдже */
  like_likers_preview?: MessengerLikeLikerPreview[];
  /** Только клиент (кэш): UI оптимистичной отправки вложения и статус доставки */
  _client?: {
    /** Стабильный key списка: тот же до/после подтверждения сервера — без remount бабла и мигания превью */
    stableBubbleKey?: string;
    attachmentPhase?: 'uploading' | 'posting';
    status?: 'failed';
    /** Данные для повторной отправки при status === 'failed' */
    retryData?: {
      body?: string;
      file_url?: string;
      file_name?: string;
      file_size?: number;
      mime_type?: string;
      context_attachments?: unknown;
      image_width?: number;
      image_height?: number;
      localImageFile?: File;
      reply_to_id?: string;
      forwarded_from_message_id?: string;
      forwarded_from_label?: string;
    };
  };
}

export interface MessengerUploadResult {
  url: string;
  key: string;
  file_name: string;
  file_size: number;
  mime_type: string;
}

/** Ответ PATCH `/agent-chats/:id` — обновление названия. */
export interface AgentChatTitlePatchResponse {
  id: string;
  title: string;
  updated_at: string;
}

/** Персистентные чаты агента «Астра» (`/api/agent-chats`). */
export interface AgentChatListItem {
  id: string;
  title: string;
  summary: string | null;
  last_message_at: string | null;
  last_model_slot: string | null;
  unread_count?: number;
  processing?: boolean;
  processing_phase?: 'submitted' | 'reasoning' | 'tool' | 'text' | null;
  created_at: string;
  updated_at: string;
}

export interface AgentChatsListResponse {
  chats: AgentChatListItem[];
  total_unread: number;
  has_more: boolean;
  next_cursor: { iso: string; id: string } | null;
}

export interface AgentChatMessageApi {
  id: string;
  ui_message_id: string;
  role: string;
  parts: unknown;
  token_estimate: number | null;
  created_at: string;
  /** true — отправка не завершилась ответом ассистента (ошибка стрима и т.п.) */
  send_failed?: boolean;
  /** Для assistant — premium | premium_pro */
  model_slot?: string | null;
}

export interface AgentChatMessagesPageResponse {
  messages: AgentChatMessageApi[];
  last_read_message_id: string | null;
  processing?: boolean;
  processing_phase?: 'submitted' | 'reasoning' | 'tool' | 'text' | null;
  has_more: boolean;
  next_before: { iso: string; id: string } | null;
}

export interface AgentChatUploadResult {
  attachment_id: string;
  url: string;
  key: string;
  file_name: string | null;
  mime_type: string | null;
  file_size: number | null;
}

export interface Author {
  id: string;
  name: string;
  created_at?: string;
}

export interface LegalEntity {
  id: string;
  name: string;
  notes?: string | null;
}

export interface LegalEntityContract {
  id: string;
  name: string;
  price: number | null;
}

export interface Studio {
  id: string;
  name: string;
  notes?: string | null;
  legal_entity?: LegalEntity | null;
}

/** @deprecated prefer LegalEntity / Studio */
export interface Contractor {
  id: string;
  legal_entity?: string | null;
  name?: string | null;
  type?: 'studio' | 'solo';
}

/** @deprecated prefer LegalEntityContract */
export interface ContractorService {
  id: string;
  service: string;
  service_price: number | null;
}

export interface ProjectContractEntryInput {
  project_contract_id?: string | null;
  legal_entity_id: string;
  contract_id?: string | null;
  name: string;
  price?: number | null;
  is_price_fixed?: boolean;
  is_included_in_budget?: boolean;
}

/** @deprecated use ProjectContractEntryInput */
export interface ContractorEntryInput {
  project_contractor_id?: string | null;
  contractor_id: string;
  service_id?: string | null;
  service_custom_price?: number | null;
  is_service_price_fixed?: boolean;
  is_contractor_included_in_budget?: boolean;
}

export interface ReaderEntryInput {
  person_id: string;
  /** Длительность по чтецу (чч:мм:сс): ключа нет — оставить как есть, null — очистить */
  reader_time?: string | null;
  /** Особая стоимость в проекте (project_readers.reader_custom_price) */
  reader_custom_price?: number | null;
  is_reader_price_fixed?: boolean;
  /** Базовая позиция каталога в проекте */
  reader_catalog_price_id?: string | null;
  /** Количество правок по чтецу в проекте: ключа нет — оставить как есть, null — очистить */
  reader_edits_number?: number | null;
  /** Авторские листы чтеца (от 0): ключа нет — оставить как есть, null — очистить */
  author_sheets?: number | null;
  /**
   * Страницы чтеца: своё число или диапазон книги (целые > 0) — что-то одно; ключа нет — оставить как есть, null — очистить.
   * Переход между своим числом и диапазоном — все три ключа сразу.
   */
  reader_pages?: number | null;
  reader_start_page?: number | null;
  reader_end_page?: number | null;
  /** Юрлицо чтеца в проекте: ключа нет — оставить как есть, null — снять */
  legal_entity_id?: string | null;
  /** Режим «Удалённая запись» (project_readers.remote_recording): ключа нет — оставить как есть */
  remote_recording?: boolean;
  /** Отметка «Запись завершена удалённо»: ключа нет — оставить как есть */
  recording_done_remote?: boolean;
  /** Отметка «Правки записаны удалённо»: ключа нет — оставить как есть */
  edits_done_remote?: boolean;
}

export interface Producer {
  id: string;
  name: string;
  dismissed?: boolean;
}

export interface Department {
  id: string;
  code: string;
}

export interface Edition {
  id: string;
  name: string;
  department_id?: string | null;
  department_code?: string | null;
}

export interface Editor {
  id: string;
  name: string;
  edition_id?: string;
  edition_name?: string;
  department_id?: string | null;
  department_code?: string | null;
  dismissed?: boolean;
}

export interface SoundEngineer {
  id: string;
  name: string;
  dismissed?: boolean;
  color?: string | null;
}

/** Тип письма (совпадает с server `emailKinds`) */
export const EMAIL_KIND_REVIEW_CHECK = 'review_check' as const;
export const EMAIL_KIND_PRODUCTION_HANDOFF = 'production_handoff' as const;
export const EMAIL_KIND_PRE_ARCHIVE_UPLOAD = 'pre_archive_upload' as const;
export const EMAIL_KIND_MOUNTING_COMPLETE = 'mounting_complete' as const;
export const EMAIL_KIND_CORRECTIONS_APPLIED = 'corrections_applied' as const;
export const EMAIL_KIND_PROJECT_REVIEWED = 'project_reviewed' as const;
export type EmailKind =
  | typeof EMAIL_KIND_REVIEW_CHECK
  | typeof EMAIL_KIND_PRODUCTION_HANDOFF
  | typeof EMAIL_KIND_PRE_ARCHIVE_UPLOAD
  | typeof EMAIL_KIND_MOUNTING_COMPLETE
  | typeof EMAIL_KIND_CORRECTIONS_APPLIED
  | typeof EMAIL_KIND_PROJECT_REVIEWED;

export interface EmailDraftResponse {
  subject: string;
  html_body: string;
}

/** Поля проекта в модалке «Проверка» (сервер `emails` draft/send) */
export interface ReviewCheckModalProjectPayload {
  /** Студия для письма (контрагент type=studio, без строк услуг) */
  studio_contractor_id: string | null;
  asta: string | null;
  edition_id: string | null;
  editor_id: string | null;
  edition_department_code: string | null;
  edition_name: string | null;
  time: string | null;
  tracks_count: number | null;
  size: number | null;
  note?: string | null;
  /**
   * UUID файлов проекта — только их ссылки попадут в письмо.
   * Если поле не передать на сервер (устаревшие клиенты), в письме останутся все файлы.
   */
  email_attachment_file_ids?: string[] | null;
  /** Письмо «Производство»: какой срок этапа уходит в письме (сервер проверяет по списку сроков) */
  deadline_field?: DeadlineField;
  /** Письмо «Производство»: дата срока (yyyy-MM-dd) — пишется в поле deadline_field проекта, null снимает срок */
  deadline_date?: string | null;
  author_sheets?: number | null;
  /** Вступительная фраза письма «Производство» (только значения из предустановок) */
  intro_phrase?: string | null;
}

/** Поля модалки «Загружен в предархив» — только редактируемые значения + комментарий и вложения */
export interface PreArchiveUploadModalProjectPayload {
  time: string | null;
  tracks_count: number | null;
  size: number | null;
  no_edits: boolean;
  note?: string | null;
  email_attachment_file_ids?: string[] | null;
}

/** Поля модалки «Монтаж завершён» (количество файлов хранится в `tracks_count` на сервере) */
export interface MountingCompleteModalProjectPayload {
  time: string | null;
  tracks_count: number | null;
  size: number | null;
  note?: string | null;
  email_attachment_file_ids?: string[] | null;
}

/** Поля модалки «Правки внесены» (после статуса «Правки: Внесены») */
export interface CorrectionsAppliedModalProjectPayload {
  note?: string | null;
  email_attachment_file_ids?: string[] | null;
  /** «Завершить проверку правок до» (ГГГГ-ММ-ДД): сервер пишет в corrections_review_deadline; null снимает срок */
  corrections_review_deadline?: string | null;
}

/** Поля модалки «Проверка завершена» (статус «Проверка: Завершена») */
export interface ProjectReviewedModalProjectPayload {
  techeditor_person_ids: string[];
  review_check_type: string | null;
  corrections_number: number | null;
  no_edits: boolean;
  note?: string | null;
  email_attachment_file_ids?: string[] | null;
}

/** Одна схема для видов писем с общей модалкой сохранения проекта (`review_check`, `production_handoff`, …). */
export interface EmailSendBody {
  kind: EmailKind;
  project_id: string;
  skip_send: boolean;
  new_state?: string;
  recipient_person_ids: string[];
  /** Копия: person_id пользователей */
  cc_person_ids?: string[];
  review_check_type?: string | null;
  review_deadline?: string | null;
  modal_project:
    | ReviewCheckModalProjectPayload
    | PreArchiveUploadModalProjectPayload
    | MountingCompleteModalProjectPayload
    | CorrectionsAppliedModalProjectPayload
    | ProjectReviewedModalProjectPayload;
  changed_by_user_id?: string;
}

export interface EmailSendResponse {
  ok: boolean;
  project: Project | null;
}

export interface Techeditor {
  id: string;
  name: string;
  email?: string | null;
}

/** Справочник корректоров — тот же контракт, что у техредакторов */
export type Corrector = Techeditor;

export interface ProjectAuthor {
  id: string;
  name: string;
  type?: 'person' | 'studio';
}

export interface ProjectContract {
  id: string;
  legal_entity_id?: string;
  project_contract_id?: string | null;
  name?: string | null;
  contract_id?: string | null;
  contract_name?: string | null;
  price?: number | null;
  catalog_price?: number | null;
  is_price_fixed?: boolean;
  is_included_in_budget?: boolean;
  /** @deprecated legacy aliases */
  legal_entity?: string | null;
  type?: 'studio' | 'solo';
  contractor_id?: string;
  service_id?: string | null;
  service_name?: string | null;
  service_price?: number | null;
  service_custom_price?: number | null;
  is_service_price_fixed?: boolean;
  is_contractor_included_in_budget?: boolean;
}

/** @deprecated use ProjectContract */
export type ProjectContractor = ProjectContract;

export interface ProjectProducer {
  id: string;
  name: string;
  email?: string | null;
}

export interface ProjectTecheditor {
  id: string;
  name: string;
  email?: string | null;
}

export type ProjectCorrector = ProjectTecheditor;

/** Допустимые значения project_type */
export type ProjectType = (typeof PROJECT_TYPE_CODES)[number];

/** Тип проверки (поле review_check_type) */
export type ReviewCheckType =
  | 'normal'
  | 'full'
  | 'accelerated'
  | 'normal_urgent'
  | 'full_urgent'
  | 'accelerated_urgent';

/** Месяц (первый день UTC, YYYY-MM-DD), в котором есть событие project_completed */
export interface ReadyMonthStatRow {
  year_month: string;
}

/** Бейджи кнопок «заметки / файлы / сессии» в таблице; полные списки подгружаются по открытию попапа. */
export interface ProjectResourceSummary {
  noteCount: number;
  recentNoteCount: number;
  fileCount: number;
  recentFileCount: number;
  recordedSessionCount: number;
  plannedSessionCount: number;
  plannerStatus: string | null;
}

/** Стадия отметки проекта (миграция 179): 0 — серая (нет), 1 — красная, 2 — жёлтая, 3 — зелёная */
export type ProjectFlagStage = 0 | 1 | 2 | 3;

/** Сроки этапов проекта (общий контракт этапов, @stages/stages): дата «ГГГГ-ММ-ДД» или null */
export type ProjectDeadlines = { [K in DeadlineField]?: string | null };

export interface Project extends ProjectDeadlines {
  id: string;
  state: string;
  /** Тип проекта: Common (Не авторский), Author (Авторский), Azbooka (Азбука), Podcast (Подкаст), Special (Спецпроект), AudioDrama (Аудиоспектакль), AI (ИИ) */
  project_type?: ProjectType | string | null;
  name: string;
  /** Короткая заметка под названием в таблице (до 40 символов) */
  name_note?: string | null;
  pages: number;
  progress: number;
  readers: ProjectReader[];
  authors?: ProjectAuthor[];
  contractors?: ProjectContract[];
  project_contracts?: ProjectContract[];
  producers?: ProjectProducer[];
  techeditors?: ProjectTecheditor[];
  correctors?: ProjectCorrector[];
  theme?: string | null;
  rights?: string | null;
  edition_id?: string | null;
  edition_name?: string | null;
  edition_department_id?: string | null;
  edition_department_code?: string | null;
  editor_id?: string | null;
  editor_name?: string | null;
  /** Тип проверки; null — не указано */
  review_check_type?: ReviewCheckType | string | null;
  /** Без правок (по умолчанию false) */
  no_edits: boolean;
  /** Количество правок по проекту (целое число) */
  corrections_number?: number | null;
  author_sheets?: number | null;
  /** Количество треков (целое число) */
  tracks_count?: number | null;
  /** Размер (десятичное число, например 0.00) */
  size?: number | null;
  asta?: string | null;
  /** Код ASE (например ASE000000000000000) */
  ase?: string | null;
  rin?: string | null;
  paper_book_plan?: string | null;
  is_backlog?: boolean;
  /** Черновик: виден только автору, в списках и таблице его нет (сервер всегда присылает флаг) */
  is_draft?: boolean;
  /** Время в формате чч:мм:сс */
  time?: string | null;
  /** Коэффициент записи (запись+правки / time), с сервера */
  recording_coef?: number | null;
  /** Коэффициент монтажа (монтаж / time), с сервера */
  montage_coef?: number | null;
  is_recording_coef_valid?: boolean;
  is_montage_coef_valid?: boolean;
  sessions?: Session[] | null;
  created_at?: string;
  /** Ревизия файлов проекта того же снимка, что files (сервер: realtime/projectWithSessions.ts, migrations/168) */
  files_revision?: number;
  updated_at?: string;
  /** Дата перехода в статус Готов (project_completed) */
  completed_at?: string | null;
  /** События смен статусов из project_status_events */
  status_events?: ProjectStatusEventBrief[];
  /** Заметки к проекту — для хранения важной информации о проекте */
  notes?: ProjectNote[];
  /** Файлы проекта, хранящиеся в S3 */
  files?: ProjectFile[];
  /** Отметки проекта — стадия (ProjectFlagStage) и комментарий; «Удалённая запись» следует за режимом чтецов (сервер) */
  project_remote?: ProjectFlagStage;
  project_phonogram?: ProjectFlagStage;
  project_dp?: ProjectFlagStage;
  project_pdf?: ProjectFlagStage;
  project_approved?: ProjectFlagStage;
  project_done?: ProjectFlagStage;
  project_remote_note?: string | null;
  project_phonogram_note?: string | null;
  project_dp_note?: string | null;
  project_pdf_note?: string | null;
  project_approved_note?: string | null;
  project_done_note?: string | null;
  /** Количество треков фонотеки, привязанных к проекту */
  fonoteka_track_count?: number;
  /** Пользовательское выделение строки в таблице (красная рамка) */
  is_project_highlighted?: boolean;
  /** Проект в UI-архиве (спойлер внизу таблицы) */
  is_archived?: boolean;
}

export interface ProjectNote {
  note_id: string;
  project_id: string;
  note_content: string;
  created_at: string;
  created_by: string | null;
  created_by_name?: string | null;
  display_order: number;
}

export interface ProjectTask {
  task_id: string;
  project_id: string;
  title: string;
  description: string;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  done_by: string | null;
  done_by_name: string | null;
  done_at: string | null;
  updated_at: string;
}

export type ProjectTaskPatch = { done: boolean } | { title: string } | { description: string };

export interface PersonNote {
  note_id: string;
  person_id: string;
  note_content: string;
  created_at: string;
  created_by: string | null;
  created_by_name?: string | null;
  display_order: number;
}

/** Снимок комментария, на который дан ответ (как reply_to в мессенджере). */
export interface CellCommentReplyTo {
  id: string;
  body: string;
  author_name: string | null;
}

/** Комментарий к ячейке таблицы проектов (project_id + column_id) */
export interface CellComment {
  id: string;
  project_id: string;
  column_id: string;
  body: string;
  /** null — автор удалён; в author_name приходит «Пользователь удалён» */
  author_person_id: string | null;
  author_name: string | null;
  created_at: string;
  /** С сервера после миграции; null/отсутствует — комментарий не редактировали */
  edited_at?: string | null;
  reply_to_id?: string | null;
  reply_to?: CellCommentReplyTo | null;
  can_delete: boolean;
  /** Окно редактирования как у сообщений мессенджера — 24 ч с created_at */
  can_edit: boolean;
}

/** Страница списка комментариев к ячейке (GET …/cell-comments) */
export interface CellCommentsListResponse {
  items: CellComment[];
  total: number;
  limit: number;
  offset: number;
}

/** Комментарий к сессии (один тред на session_id, без column_id) */
export interface SessionComment {
  id: string;
  session_id: string;
  body: string;
  author_person_id: string | null;
  author_name: string | null;
  created_at: string;
  edited_at?: string | null;
  reply_to_id?: string | null;
  reply_to?: CellCommentReplyTo | null;
  can_delete: boolean;
  can_edit: boolean;
}

export interface SessionCommentsListResponse {
  items: SessionComment[];
  total: number;
  limit: number;
  offset: number;
}

/** Комментарий к ячейке таблицы чтецов (reader_person_id + column_id) */
export interface ReaderCellComment {
  id: string;
  reader_person_id: string;
  column_id: string;
  body: string;
  author_person_id: string | null;
  author_name: string | null;
  created_at: string;
  edited_at?: string | null;
  reply_to_id?: null;
  reply_to?: null;
  can_delete: boolean;
  can_edit: boolean;
}

export interface ReaderCellCommentsListResponse {
  items: ReaderCellComment[];
  total: number;
  limit: number;
  offset: number;
}

export interface ProjectFile {
  file_id: string;
  project_id: string;
  file_name: string;
  file_url: string;
  file_size: number | null;
  uploaded_at: string;
  uploaded_by: string | null;
  uploaded_by_name?: string | null;
  display_order: number;
  file_type: string | null;
  file_author_sheets?: number | string | null;
  file_pages?: number | null;
  /**
   * Можно ли открыть в ридере (решает сервер, по расширению исходного файла в хранилище): pdf — как есть,
   * document — Word и похожие, сервер переводит в PDF; sheet — таблица (xlsx, xls, ods, csv), ридер таблиц.
   * null или нет поля — без просмотра.
   */
  preview_kind?: ProjectFilePreviewKind | null;
}

export type ProjectFilePreviewKind = 'pdf' | 'document' | 'sheet';

/** Коды ошибок адреса просмотра (server/src/services/filePreview/previewKind.ts, PREVIEW_ERROR_CODES) */
export type ProjectFilePreviewErrorCode = 'PREVIEW_UNSUPPORTED' | 'PREVIEW_CONVERSION_FAILED' | 'PREVIEW_CONVERTER_UNAVAILABLE';

/** Файл чтеца в каталоге (reader_files + S3). */
export interface ReaderFile {
  file_id: string;
  reader_person_id: string;
  file_name: string;
  file_url: string;
  file_size: number | null;
  uploaded_at: string;
  uploaded_by: string | null;
  uploaded_by_name?: string | null;
  display_order: number;
}

export interface ProjectReader {
  id: string;
  name: string;
  progress?: number;
  /** Фактически прочитано страниц (denormalized SSOT, project_readers.pages_read) */
  pages_read?: number;
  /** Сессии «запись» и «правки» с этим чтецом в проекте (с сервера READERS_AGG) */
  recording_sessions_count?: number;
  /** Темп записи: целых страниц в час (denormalized SSOT, project_readers.avg_pages_per_hour) */
  avg_pages_per_hour?: number | string | null;
  /** Оставшиеся условные сессии (1 = 3.5 ч = coef 1) */
  remaining_sessions?: number | string | null;
  /** Длительность по чтецу (чч:мм:сс) */
  reader_time?: string | null;
  /** Особая стоимость в проекте (project_readers.reader_custom_price) */
  reader_custom_price?: number | null;
  is_reader_price_fixed?: boolean;
  /** Выбранная базовая позиция каталога (project_readers.reader_catalog_price_id) */
  reader_catalog_price_id?: string | null;
  /** Позиции цен из каталога чтеца (READERS_AGG) */
  reader_prices?: ReaderCatalogPrice[];
  /** Цена выбранной позиции каталога (для подсказок) */
  effective_catalog_price?: number | string | null;
  reader_coef?: number | null;
  is_reader_coef_valid?: boolean;
  /** Количество правок по чтецу в проекте */
  reader_edits_number?: number | null;
  /** Авторские листы чтеца в проекте (project_readers.author_sheets); null — не указаны */
  author_sheets?: number | null;
  /** Своё число страниц чтеца (project_readers.reader_pages): читает с 1-й по него; null — не задано */
  reader_pages?: number | null;
  /** Диапазон страниц книги чтеца (reader_start_page / reader_end_page); null — граница не задана. С reader_pages не сочетается */
  reader_start_page?: number | null;
  reader_end_page?: number | null;
  /** Юрлицо чтеца в этом проекте (project_readers.legal_entity_id) */
  legal_entity_id?: string | null;
  legal_entity_name?: string | null;
  /** Студия чтеца из каталога (readers.studio_id), если есть */
  studio?: { id: string; name: string } | null;
  /** Собственное юрлицо чтеца в каталоге (readers.legal_entity_id) — вариант поля «Юрлицо» в проекте */
  catalog_legal_entity?: { id: string; name: string } | null;
  /** Юрлицо студии чтеца в каталоге (studios.legal_entity_id) — вариант поля «Юрлицо» в проекте */
  studio_legal_entity?: { id: string; name: string } | null;
  /** Режим «Удалённая запись»: чтец пишет не у нас — без «Не запланировано», планировщик его не считает (project_readers.remote_recording) */
  remote_recording?: boolean;
  /** «Запись завершена удалённо»: запись чтеца считается сделанной на 100 % при любом режиме (project_readers.recording_done_remote) */
  recording_done_remote?: boolean;
  /** «Правки записаны удалённо»: записаны все назначенные правки при любом режиме (project_readers.edits_done_remote) */
  edits_done_remote?: boolean;
}

export interface CreateProjectData extends ProjectDeadlines {
  name: string;
  state?: string;
  project_type?: ProjectType | null;
  pages?: number;
  /** Тип проверки; null — не указано */
  review_check_type?: ReviewCheckType | string | null;
  /** Без правок (по умолчанию false) */
  no_edits?: boolean;
  /** Количество правок по проекту (целое число) */
  corrections_number?: number | null;
  author_sheets?: number | null;
  tracks_count?: number | null;
  size?: number | null;
  asta?: string | null;
  /** Код ASE (например ASE000000000000000) */
  ase?: string | null;
  rin?: string | null;
  paper_book_plan?: string | null;
  is_backlog?: boolean;
  theme?: string | null;
  rights?: string | null;
  edition_id?: string | null;
  editor_id?: string | null;
  author_ids?: string[];
  reader_ids?: string[];
  reader_entries?: ReaderEntryInput[];
  contractor_ids?: string[];
  contractor_entries?: ContractorEntryInput[];
  project_contracts?: ProjectContractEntryInput[];
  producer_ids?: string[];
  techeditor_ids?: string[];
  corrector_ids?: string[];
  changed_by_user_id?: string | null;
}

export interface UpdateProjectData extends ProjectDeadlines {
  name?: string;
  name_note?: string | null;
  state?: string;
  project_type?: ProjectType | null;
  pages?: number;
  /** Тип проверки; null — не указано */
  review_check_type?: ReviewCheckType | string | null;
  /** Без правок (по умолчанию false) */
  no_edits?: boolean;
  /** Количество правок по проекту (целое число) */
  corrections_number?: number | null;
  author_sheets?: number | null;
  tracks_count?: number | null;
  size?: number | null;
  asta?: string | null;
  /** Код ASE (например ASE000000000000000) */
  ase?: string | null;
  rin?: string | null;
  paper_book_plan?: string | null;
  is_backlog?: boolean;
  /** Время в формате чч:мм:сс */
  time?: string | null;
  theme?: string | null;
  rights?: string | null;
  edition_id?: string | null;
  editor_id?: string | null;
  author_ids?: string[];
  reader_ids?: string[];
  reader_entries?: ReaderEntryInput[];
  contractor_ids?: string[];
  contractor_entries?: ContractorEntryInput[];
  project_contracts?: ProjectContractEntryInput[];
  producer_ids?: string[];
  techeditor_ids?: string[];
  corrector_ids?: string[];
  changed_by_user_id?: string | null;
  project_remote?: ProjectFlagStage;
  project_phonogram?: ProjectFlagStage;
  project_dp?: ProjectFlagStage;
  project_pdf?: ProjectFlagStage;
  project_approved?: ProjectFlagStage;
  project_done?: ProjectFlagStage;
  /** Комментарий отметки: пустая строка или null — убрать */
  project_remote_note?: string | null;
  project_phonogram_note?: string | null;
  project_dp_note?: string | null;
  project_pdf_note?: string | null;
  project_approved_note?: string | null;
  project_done_note?: string | null;
  is_project_highlighted?: boolean;
  is_archived?: boolean;
}

/** Элемент status_events в составе проекта (список/карточка) */
export interface ProjectStatusEventBrief {
  id?: string;
  event_type: string;
  occurred_at: string;
  changed_by_person_id?: string | null;
  changed_by_name?: string | null;
}

export interface ProjectStatusEvent {
  id: string;
  event_type: string;
  occurred_at: string;
  changed_by_user_id: string | null;
  changed_by_person_id?: string | null;
}

/** Типы сессий: запись, правки, монтаж, заметка */
export const SESSION_TYPES = ['запись', 'правки', 'монтаж', 'заметка'] as const;
export type SessionType = (typeof SESSION_TYPES)[number];
export type SessionSubtype = 'podcast';

export interface SessionReader {
  id: string;
  name: string;
  progress?: number | null;
}

/**
 * Сессия может быть без проекта (project_id = null) для типов запись/правки,
 * но в этом случае обязательно указывается хотя бы одна персона через reader_ids.
 */
export interface Session {
  id: string;
  date: string;
  start: string;
  end: string;
  studio: string;
  project_id: string | null;
  project_name?: string | null;
  project_type?: ProjectType | string | null;
  project_progress?: number;
  reader_progress?: number;
  readers?: SessionReader[];
  reader_ids?: string[];
  reader_id?: string | null;
  reader_name?: string | null;
  soundengineer_id?: string | null;
  soundengineer_name?: string | null;
  soundengineer_color?: string | null;
  coef?: number;
  is_recorded?: boolean;
  is_partial?: boolean;
  /** Сессия отменена */
  is_canceled?: boolean;
  /** Причина отмены, до 300 символов */
  canceled_note?: string | null;
  /** Отменённые сессии скрыта из сетки (остаётся в списке «Отменённые сессии») */
  canceled_hidden_from_schedule?: boolean;
  /** Момент отмены (UTC); сбрасывается при восстановлении сессии */
  cancelled_at?: string | null;
  stop_page?: number | null;
  /** Сколько правок записано за сессию «правки» (целое от 1; «Все» — числом на момент внесения); у других типов — null */
  corrections_recorded?: number | null;
  session_type?: SessionType | string;
  session_subtype?: SessionSubtype | string | null;
  title?: string | null;
  content?: string | null;
  font_size?: number | null;
  is_header_hidden?: boolean | null;
  is_reader_hidden?: boolean | null;
  is_project_hidden?: boolean | null;
  /** Скрыть футер с прогрессом (сессии «запись») */
  is_progress_hidden?: boolean | null;
  second_project_font_size?: number | null;
  created_at?: string;
  updated_at?: string;
  /** Вычисляемое клиентом: сессия является финальной для проекта (первая, где accumulated coef > remaining_sessions) */
  is_final_session?: boolean;
  /** Вычисляемое клиентом: сессия выходит за пределы remaining_sessions (после финальной) */
  is_excess_session?: boolean;
  /** Вычисляемое клиентом: доля высоты пузырька, принадлежащая текущему проекту (0..1) */
  _split_ratio?: number;
  /** Вычисляемое клиентом: имена следующих проектов чтеца; null = «Нет проекта» */
  _next_project_names?: string[] | null;
}

export type {
  AgentChatModelOption,
  AgentChatQuotaSnapshot,
  ChatModelId,
} from '@/components/Messenger/agent/agentChatSharedTypes';
